/**
 * Slow Maui Public Site
 *
 * Main entry point for the public-facing site.
 */

import { DataClient } from '@slowmaui/api-client';
import { initPersistentHeader } from './header';
import {
  initializeFirebase,
  onAuthStateChange,
  onIdTokenChange,
  signInWithGoogle,
  signInWithApple,
  signOut,
  type AuthUser,
} from '@slowmaui/auth';
import {
  SlowMauiMap,
  DisplayDrawer,
  ListingsDrawer,
  PropertyModal,
  FilterDrawer,
  CNTLDrawer,
  NeighborhoodModal,
  GeographyModal,
  ZoningLegend,
  MarketTrendsLegend,
  ClimateLegend,
  UnifiedDrawer,
  AskOverlay,
  ATTRIBUTE_REGISTRY,
  formatRelativeDate,
} from '@slowmaui/ui-components';
import type { PropertyFilters, DisplayOptions } from '@slowmaui/ui-components';
import type { NeighborhoodFillMode, ClimateDatatype } from '@slowmaui/ui-components';
import type { TmkStatusMarker, AttributeIdSets } from '@slowmaui/shared-types';
import type { StatusFilters, AttributeFilters } from '@slowmaui/ui-components';
import type { AskSearchResponse, AskSearchNeighborhood } from '@slowmaui/ui-components';
import type { NeighborhoodSummary, ListingSummary, PropertyModalData, MLSListing, Neighborhood } from '@slowmaui/shared-types';
import { formatNeighborhoodName, getZoningCategory, formatListingAddress, factsFromListingSummary, listingItemKey } from '@slowmaui/shared-types';
import type { NeighborhoodSalesChartData, GeographySummary, GeographyModalData, GeographyEntityType, MarketTrendsChartData, PriceMetric } from '@slowmaui/ui-components';
import { initRouter } from './router';
import * as AccountPage from './pages/Account';
import { sortListings } from './utils/sortListings';
import {
  initFavorites,
  initFavoritesForUser,
  resetFavoritesToAnonymous,
  getFavorites,
  isFavorite,
  toggleFavorite,
  onFavoritesChange,
  getLocalStorageFavorites,
  clearLocalStorageFavorites,
} from './utils/favorites';
import {
  initAlerts,
  initAlertsForUser,
  resetAlertsToAnonymous,
  getAlerts,
  hasAlert,
  toggleAlert,
  canCreateAlerts,
  onAlertsChange,
} from './utils/alerts';
import {
  initAgentPicks,
  getAgentPicks,
  getActiveStoryMeta,
  onAgentPicksChange,
  checkUrlForStory,
  activateStory,
  getAgentPickHistory,
} from './utils/agentPicks';
import {
  initAgentFeatured,
  getAgentFeatured,
  getActiveFeaturedMeta,
  onAgentFeaturedChange,
  checkUrlForAgent,
} from './utils/agentFeatured';
import { getIdToken } from '@slowmaui/auth';

// Configuration
const CONFIG = {
  apiUrl: import.meta.env.VITE_DATA_API_URL || import.meta.env.VITE_API_URL || 'https://api.slowmaui.com',
  firebase: {
    apiKey: import.meta.env.VITE_FIREBASE_API_KEY || '',
    authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN || '',
    projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID || '',
    appId: import.meta.env.VITE_FIREBASE_APP_ID || '',
  },
};

// Feature flags - cheap kill switches for in-progress map features
const FEATURE_FLAGS = {
  // Fake 2.5D building-height effect in neighborhood mode. Flip to false to
  // disable instantly without touching Map.ts.
  buildingExtrusion: true,
  // Experimental: show unit numbers on off-market NHM markers (faded
  // circle + unit text) instead of a plain gray dot. Flip to false to
  // revert instantly without touching Map.ts.
  offMarketUnitLabels: true,
};

// Initialize API client
const api = new DataClient({
  baseUrl: CONFIG.apiUrl,
  debug: import.meta.env.DEV,
});

// Every authenticated request now re-fetches a live token right before
// firing (cheap no-op if Firebase's cached one is still valid, silent
// refresh otherwise) instead of trusting whatever setAuthToken() last
// cached - closes the "signed in but a request still 401s" gap for good,
// rather than depending on onIdTokenChange firing in time (see its own
// comment below - that listener's re-cache is now a backstop, not the
// only path to a fresh token).
api.setAuthTokenProvider(() => getIdToken());

// Data freshness state
let dataAvailable = true;

// State
let map: SlowMauiMap | null = null;
// cntlDrawer + listingsDrawer are mounted as embedded sections inside unifiedDrawer.
let cntlDrawer: CNTLDrawer | null = null;
let listingsDrawer: ListingsDrawer | null = null;
let unifiedDrawer: UnifiedDrawer | null = null;
let displayDrawer: DisplayDrawer | null = null;
let filterDrawer: FilterDrawer | null = null;
let propertyModal: PropertyModal | null = null;
let neighborhoodModal: NeighborhoodModal | null = null;
// Single shared instance for both Region and District label clicks - the
// component doesn't care which (entityType only affects display), and the
// wiring below picks the right API calls per open() based on the
// GeographyModalData.entityType passed in.
let geographyModal: GeographyModal | null = null;
let zoningLegend: ZoningLegend | null = null;
let marketTrendsLegend: MarketTrendsLegend | null = null;
let climateLegend: ClimateLegend | null = null;
// Fetched lazily the first time either Market Trends fill is turned on, not
// on every page load - cached after that so re-toggling doesn't refetch.
let priceHeatmapData: Awaited<ReturnType<typeof api.getPriceHeatmap>> | null = null;
let priceHeatmapLoading = false;
// Which climate datatypes have already been fetched (both neighborhood and
// district entity types) and pushed into the map - same lazy-cache idea as
// price above, just per-datatype since /climate is one call per
// (entityType, datatype) rather than one call covering everything.
const climateDataLoaded = new Set<ClimateDatatype>();
const climateSourceByDatatype: Partial<Record<ClimateDatatype, Awaited<ReturnType<typeof api.getClimateData>>['source']>> = {};
let listings: ListingSummary[] = [];
// Fast-path data driving the map's bubbles/markers - fetched from the
// precomputed tmk_status table (see loadMapMarkers) instead of the
// mls_listings-direct endpoint `listings` above uses. Kept as a SEPARATE
// array (not a replacement for `listings`) so the map can render as soon as
// this resolves without waiting on the richer per-listing data
// (address/photos/exact specs) the Listings Drawer needs - see
// loadListingsForDrawer. Ask mode and NHM are unaffected: both already have
// their own dedicated data paths.
let mapMarkerListings: ListingSummary[] = [];
// True once loadListingsForDrawer has resolved at least once for the
// current status/attribute selection - drives the drawer's loading spinner
// (see ListingsDrawer.setLoading) independent of whether the map already
// has bubbles.
let drawerListingsReady = false;
let neighborhoods: NeighborhoodSummary[] = [];
let counties: GeographySummary[] = [];
let regions: GeographySummary[] = [];
let districts: GeographySummary[] = [];
let currentFilters: PropertyFilters = {};
let filtersEnabled = true;
// Keyword filter's server-resolved match set (tmkNormalized values) - null
// means "no keyword active, don't restrict"; an empty Set means "keyword
// active, matched nothing." Populated async by handleFilterChange since,
// unlike every other filter, remarks text isn't in the fast marker payload
// (see data-api's GET /listings/search-ids).
let keywordMatchTmks: Set<string> | null = null;
// Ask results are mutually exclusive with the regular structured filters -
// active Ask results replace what's shown on the map/sidebar outright
// rather than being intersected with price/beds/type filters.
let askModeActive = false;
let askResultListings: ListingSummary[] = [];
let askResultSummary: string | null = null;
let askResultBannerEl: HTMLElement | null = null;
// Snapshot of the real `listings` array taken at Ask activation, since Ask
// mode replaces `listings` outright with its own result set - restored on
// exit so the regular pipeline goes back to the full dataset.
let preAskListings: ListingSummary[] | null = null;
let neighborhoodsEnabled = true;
let currentNeighborhoodMode: { id: string; name: string } | null = null;

// Coordinates + listingItemKey() of whichever property PropertyModal
// currently has open, set at every modal-open call site. On close (see
// initPropertyModal's onClose) this drives recentering the map on that
// property and leaving Map.highlightListing's persistent marker treatment
// on it - the mobile substitute for ListingsDrawer's desktop hover, since
// touch has no mouseenter/mouseleave to hang that off of. Overwritten (not
// explicitly cleared) by the next open, which is also what makes the
// highlight naturally swap to whatever's opened next. `id` here must be
// the same TMK-based key Map.highlightListing expects - see its comment
// for why a raw ListingSummary.id doesn't reliably match a marker.
let openPropertyMapTarget: { id: string; lat: number; lng: number } | null = null;

// Viewport bounds for filtering listings
type MapBounds = { north: number; south: number; east: number; west: number };
let currentViewportBounds: MapBounds | null = null;
const VIEWPORT_FILTER_MIN_ZOOM = 14; // Only filter by viewport when zoomed in past this level
let viewportReloadTimer: ReturnType<typeof setTimeout> | null = null;
const VIEWPORT_RELOAD_DEBOUNCE_MS = 300; // Coalesce rapid successive pans into one server fetch

// The (padded) region actually held in `listings` right now, and when it was
// fetched. A pan that stays within this padded region can render from
// already-downloaded data instead of hitting the network again - only a pan
// that exits it, or data older than the max age, triggers a real re-fetch.
let lastFetchedListingsBounds: MapBounds | null = null;
let lastFetchedListingsAt = 0;
// True when the last fetch had no bounds (island-wide - zoomed out past
// VIEWPORT_FILTER_MIN_ZOOM, or Favorites/Alerts active). Needed because an
// unbounded fetch already covers every possible viewport - without this,
// scheduleViewportReload had no way to tell "we have everything" apart from
// "we have nothing yet", and treated every pan while zoomed out as a cache
// miss requiring a fresh island-wide re-fetch. Confirmed live 2026-09: this
// caused a full ~1.5MB re-fetch + full reclustering + full sidebar re-render
// on every single pan-settle at the default zoom level - the reported
// mobile pan freeze.
let lastFetchWasUnbounded = false;
const VIEWPORT_PREFETCH_PADDING = 0.75; // Fetch ~2.5x the visible area on each axis
const LISTINGS_CACHE_MAX_AGE_MS = 5 * 60 * 1000; // Matches the data-api /listings KV cache TTL

function padBounds(b: MapBounds): MapBounds {
  const latSpan = b.north - b.south;
  const lngSpan = b.east - b.west;
  return {
    north: b.north + latSpan * VIEWPORT_PREFETCH_PADDING,
    south: b.south - latSpan * VIEWPORT_PREFETCH_PADDING,
    east: b.east + lngSpan * VIEWPORT_PREFETCH_PADDING,
    west: b.west - lngSpan * VIEWPORT_PREFETCH_PADDING,
  };
}

function boundsContain(outer: MapBounds, inner: MapBounds): boolean {
  return (
    inner.north <= outer.north &&
    inner.south >= outer.south &&
    inner.east <= outer.east &&
    inner.west >= outer.west
  );
}

/**
 * Bounds to pass to the listings API for the current view, or undefined to
 * fetch island-wide. Server-side bounding only kicks in when zoomed in past
 * VIEWPORT_FILTER_MIN_ZOOM (matches the zoom the old client-side viewport
 * filter used) AND Favorites/Alerts aren't active - those are "show me MY
 * saved stuff" views where a user panning around should still see
 * everything they've saved, not just what's on screen right now. Padded
 * beyond the exact viewport (see scheduleViewportReload) so most small pans
 * don't need a new fetch at all - the existing client-side viewport filter
 * in applyFiltersToListings() narrows the padded set back down for display.
 */
function getListingsFetchBounds(): MapBounds | undefined {
  if (!currentViewportBounds) return undefined;
  const currentZoom = map?.getZoom() ?? 0;
  if (currentZoom < VIEWPORT_FILTER_MIN_ZOOM) return undefined;
  const attributes = cntlDrawer?.getAttributes();
  // id-set attributes (Favorites/Alerts, and any future one like Agent
  // Recommended) are "show me MY saved stuff" views, not "what's on
  // screen" - exempt from the viewport bound for all of them, not just
  // today's two.
  if (attributes && ATTRIBUTE_REGISTRY.some((entry) => entry.kind === 'id-set' && attributes[entry.id])) {
    return undefined;
  }
  return padBounds(currentViewportBounds);
}

// Auth state
let currentUser: AuthUser | null = null;

// True when the contact modal was closed in favor of the auth modal (because
// the user clicked "Sign in to contact"). On the next successful sign-in we
// auto-reopen the contact modal so the user lands back where they wanted.
let pendingContactReopen = false;

// Set by PropertyModal's "Contact Agent" button to seed the contact form's
// message field with property context (e.g., "I'm interested in 123 Main St").
// Consumed once on the next refreshContactModalState() — survives the
// auth-gate detour because that triggers a re-render which checks this.
let pendingContactPrefill: string | null = null;

function escapeHtmlText(s: string | null | undefined): string {
  if (s == null) return '';
  return String(s).replace(/[&<>"']/g, ch => (
    { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[ch]!
  ));
}

/**
 * Check if MLS data is fresh (< 12 hours old)
 * Shows unavailable modal if data is stale
 */
async function checkDataFreshness(): Promise<boolean> {
  try {
    const response = await fetch(`${CONFIG.apiUrl}/status`);
    if (!response.ok) {
      console.warn('Status endpoint not available, assuming data is fresh');
      return true;
    }

    const result = await response.json();
    if (result.success && result.data) {
      const { available, reason, message } = result.data;

      if (!available) {
        console.warn(`MLS data unavailable: ${reason} - ${message}`);
        showUnavailableModal();
        return false;
      }

      console.log('MLS data is fresh:', result.data.lastSyncAt);
      return true;
    }

    return true;
  } catch (error) {
    console.error('Failed to check data freshness:', error);
    // On error, allow the app to continue (don't block on status check failure)
    return true;
  }
}

/**
 * Show the unavailable modal (blocks app until data is fresh)
 */
function showUnavailableModal(): void {
  const modal = document.getElementById('unavailable-modal');
  modal?.classList.remove('hidden');
  dataAvailable = false;
}

/**
 * Initialize the app
 *
 * Performance optimized: UI components initialize immediately while
 * map and data load in parallel in the background.
 */
// Embed mode: when slowmaui.com is loaded with ?embed=1&n=<slug> (e.g. inside
// an SEO-page iframe), strip chrome, lock to neighborhood mode for that slug,
// and intercept all clicks to navigate the parent window to the live site.
//
// `n` is the canonical query-param for neighborhood deep-links — used by the
// SEO page's mainSiteLink CTAs and the embed iframe URL. `nh` is accepted as
// a fallback so pages published before the rename still resolve correctly.
// Three deep-link param shapes are recognized:
//   ?n=<slug> → neighborhood    (also ?nh= as legacy fallback)
//   ?d=<slug> → district
//   ?r=<slug> → region
// Combined with ?embed=1 these power the iframe map embedded on each
// SEO page level so the map opens scoped to the relevant entity.
type DeepLinkScope =
  | { type: 'neighborhood'; slug: string }
  | { type: 'district'; slug: string }
  | { type: 'region'; slug: string }
  | null;

function readDeepLinkScope(params: URLSearchParams): DeepLinkScope {
  const n = params.get('n') || params.get('nh');
  if (n) return { type: 'neighborhood', slug: n };
  const d = params.get('d');
  if (d) return { type: 'district', slug: d };
  const r = params.get('r');
  if (r) return { type: 'region', slug: r };
  return null;
}

function getEmbedParams(): { embed: boolean; scope: DeepLinkScope } {
  const params = new URLSearchParams(window.location.search);
  return {
    embed: params.get('embed') === '1',
    scope: readDeepLinkScope(params),
  };
}

/**
 * In embed mode, drop a transparent absolutely-positioned div on top of the
 * map. It captures every pointer event (click, drag, wheel, touch) and
 * navigates the parent window to the live site. Replaces a noisier approach
 * of disabling individual map gestures.
 */
function installEmbedClickShield(): void {
  const params = new URLSearchParams(window.location.search);
  const scope = readDeepLinkScope(params);
  // Click target depends on which scope the iframe is showing — bring the
  // user to the same scope on the live site, not a generic home view.
  const paramFor: Record<NonNullable<DeepLinkScope>['type'], string> = {
    neighborhood: 'n',
    district: 'd',
    region: 'r',
  };
  const targetUrl = scope
    ? `https://slowmaui.com/?${paramFor[scope.type]}=${encodeURIComponent(scope.slug)}`
    : 'https://slowmaui.com/';

  const navigateTop = () => {
    try {
      if (window.top && window.top !== window.self) {
        window.top.location.href = targetUrl;
        return;
      }
    } catch {
      // Cross-origin top access blocked — fall through.
    }
    window.location.href = targetUrl;
  };

  const mapContainer = document.getElementById('map-container');
  if (!mapContainer) return;

  const shield = document.createElement('div');
  shield.id = 'embed-click-shield';
  Object.assign(shield.style, {
    position: 'absolute',
    inset: '0',
    zIndex: '9999',
    cursor: 'pointer',
    background: 'transparent',
  });
  // Ensure mapContainer establishes a positioning context for `inset: 0`.
  if (getComputedStyle(mapContainer).position === 'static') {
    mapContainer.style.position = 'relative';
  }
  mapContainer.appendChild(shield);

  // `click` handles mouse + touch-tap. wheel/touchmove explicitly stopped so
  // pinch/scroll attempts don't even register on the map below.
  shield.addEventListener('click', (e) => {
    e.preventDefault();
    e.stopPropagation();
    navigateTop();
  });
  for (const evt of ['wheel', 'touchstart', 'touchmove', 'mousedown'] as const) {
    shield.addEventListener(
      evt,
      (e: Event) => {
        e.preventDefault();
        e.stopPropagation();
      },
      { passive: false },
    );
  }
}

// Fit the map to the bounding box of an array of lat/lng points. Used for
// district / region deep-links where we don't enter "neighborhood mode" but
// still want the camera scoped to the entity. Skips silently on empty input
// so callers don't have to guard.
const MAX_FIT_ZOOM = 16;

function fitMapToPoints(points: Array<{ lat: number; lng: number }>): void {
  if (!map) return;
  // A NaN/non-finite point fed into LatLngBounds produces a degenerate
  // bbox that fitBounds resolves by zooming out to the whole-Earth view -
  // filter those out here rather than trusting every caller to have
  // already done it.
  const finitePoints = points.filter((p) => Number.isFinite(p.lat) && Number.isFinite(p.lng));
  if (finitePoints.length === 0) return;
  const bounds = new google.maps.LatLngBounds();
  for (const p of finitePoints) bounds.extend(p);
  map.fitBounds(bounds);

  // fitBounds has no built-in max zoom - a tightly clustered result set
  // (e.g. every unit in one building) computes a near-zero-area bbox,
  // which Google Maps zooms to its absolute max for (rooftop level).
  // Cap it at a reasonable "see the building plus its neighborhood" level
  // instead - 'idle' fires once the fit completes, same pattern Google's
  // own docs recommend for reading back the zoom fitBounds picked.
  const gmap = map.getMap();
  if (gmap) {
    google.maps.event.addListenerOnce(gmap, 'idle', () => {
      const zoom = gmap.getZoom();
      if (zoom != null && zoom > MAX_FIT_ZOOM) {
        gmap.setZoom(MAX_FIT_ZOOM);
      }
    });
  }
}

// Apply a deep-link scope by entering neighborhood mode (when scope is a
// neighborhood) or fitting the camera to the entity (region / district).
async function applyDeepLinkScope(scope: NonNullable<DeepLinkScope>): Promise<void> {
  if (!map) return;

  if (scope.type === 'neighborhood') {
    await ensureFullNeighborhoodsLoaded();
    const nh = neighborhoods.find((n) => n.slug === scope.slug);
    if (nh) {
      map.enterNeighborhoodMode(nh.id);
      currentNeighborhoodMode = { id: nh.id, name: nh.name };
      applyFiltersToListings();
    } else {
      console.warn(`[deep-link] Neighborhood "${scope.slug}" not found`);
    }
    return;
  }

  if (scope.type === 'district') {
    try {
      const district = await api.getDistrict(scope.slug);
      if (district.polygon?.length) {
        fitMapToPoints(district.polygon);
      } else {
        console.warn(`[deep-link] District "${scope.slug}" has no polygon`);
      }
    } catch (e) {
      console.warn(`[deep-link] Failed to load district "${scope.slug}":`, e);
    }
    return;
  }

  // region
  try {
    const regionNhs = await api.getRegionNeighborhoods(scope.slug);
    const points = regionNhs.flatMap((n) => n.polygon || []);
    if (points.length) {
      fitMapToPoints(points);
    } else {
      console.warn(`[deep-link] Region "${scope.slug}" has no polygons to fit to`);
    }
  } catch (e) {
    console.warn(`[deep-link] Failed to load region "${scope.slug}":`, e);
  }
}

// `?n=<slug>&tab=activity` deep link (see workers/notifications' "Change"
// subscription emails) - opens the NeighborhoodModal directly on its Latest
// Activity tab, skipping the map-zoom-only neighborhood mode entirely
// (applyDeepLinkScope's neighborhood branch) since the email recipient
// wants to see what changed, not look at the map.
async function openNeighborhoodActivityFromDeepLink(slug: string): Promise<void> {
  await ensureFullNeighborhoodsLoaded();
  const nh = neighborhoods.find((n) => n.slug === slug);
  if (!nh) {
    console.warn(`[deep-link] Neighborhood "${slug}" not found`);
    return;
  }
  neighborhoodModal?.open(
    {
      id: nh.id,
      name: nh.name,
      regionName: nh.regionName,
      primaryImageUrl: nh.primaryImageUrl,
      totalUnits: nh.totalUnits,
      forSaleCount: nh.forSaleCount,
    },
    'latestActivity'
  );
}

async function init(): Promise<void> {
  // Capture deep-link params at the very top — they're consumed at the END
  // of init, but if any code along the way (e.g. router's home handler
  // triggering modal.close → onClose → history.pushState('/')) nukes the URL,
  // reading window.location.search later would return empty.
  const initialSearch = new URLSearchParams(window.location.search);
  const initialDeepLink = {
    scope: readDeepLinkScope(initialSearch),
    propertyMlsId: initialSearch.get('p'),
    // `?n=<slug>&tab=activity` - used by the "Change" subscription emails
    // (see workers/notifications) to land directly on a neighborhood's
    // Latest Activity tab instead of just entering neighborhood mode.
    tab: initialSearch.get('tab'),
    // `?auth=1` - the static agent custom pages' "Login" nav link
    // (workers/agent-api/src/pageChrome.js's buildNav()) points here rather
    // than opening a real auth modal on the static page itself - those
    // pages have no Firebase/auth capability by design (loading it there
    // would tax every static page view for a feature most visitors won't
    // use). Consumed at the end of init(), once #auth-modal is wired up.
    auth: initialSearch.get('auth') === '1',
  };

  // Embed-mode setup runs first so the chrome is hidden before anything
  // paints. Click interception is handled by a transparent shield installed
  // over the map after it's ready (see installEmbedClickShield below).
  const embedParams = getEmbedParams();
  if (embedParams.embed) {
    document.body.classList.add('embed-mode');
  }

  // Check data freshness in the background rather than blocking on it - this
  // is a full extra network round-trip, and awaiting it here (as before)
  // held up every other init step, adding 200-800ms of dead time on mobile
  // for what's almost always a no-op check. checkDataFreshness() shows its
  // own blocking overlay via showUnavailableModal() if data turns out to be
  // stale, which is enough to halt the user without holding up first paint
  // for everyone else in the meantime.
  void checkDataFreshness();

  // Initialize favorites and alerts (before auth so anonymous data works)
  initFavorites();
  initAlerts();
  // Agent Picks - restores whatever story link was last opened, if any.
  // checkUrlForStory() (called later, once `api` is ready) handles a fresh
  // `?story=` param specifically; this just restores prior state so the
  // attribute isn't empty on a plain reload.
  initAgentPicks();
  // Featured - same restore-on-load pattern, see checkUrlForAgent() below.
  initAgentFeatured();

  // Initialize Firebase and auth
  if (CONFIG.firebase.apiKey) {
    initializeFirebase(CONFIG.firebase);
    initAuth();
  }

  // Subscribe to favorites changes to update UI
  onFavoritesChange((favorites) => {
    // Update map badge options with new favorites
    if (map && cntlDrawer) {
      const attributes = cntlDrawer.getAttributes();
      map.setBadgeOptions({
        show: attributes,
        favoriteIds: favorites,
        alertIds: getAlerts(),
        agentPickIds: getAgentPicks(),
        featuredIds: getAgentFeatured(),
      });
    }
    // Update listings drawer with new favorites
    listingsDrawer?.setFavorites(favorites);
    // Re-apply filters if favorites attribute is active
    if (cntlDrawer?.getAttributes().favorites) {
      applyFiltersToListings();
    }
  });

  // Subscribe to alerts changes to update UI
  onAlertsChange((alerts) => {
    // Update map badge options with new alerts
    if (map && cntlDrawer) {
      const attributes = cntlDrawer.getAttributes();
      map.setBadgeOptions({
        show: attributes,
        favoriteIds: getFavorites(),
        alertIds: alerts,
        agentPickIds: getAgentPicks(),
        featuredIds: getAgentFeatured(),
      });
    }
    // Update listings drawer with new alerts
    listingsDrawer?.setAlerts(alerts);
    // Re-apply filters if alerts attribute is active
    if (cntlDrawer?.getAttributes().alerts) {
      applyFiltersToListings();
    }
  });

  // Subscribe to Agent Picks changes (opening/reactivating a ?story= link,
  // or the active one expiring) to update UI - same shape as favorites/
  // alerts above, plus the attribute's own visibility (only shown at all
  // while a pick is active). listingsDrawer.setAgentPicks() below triggers
  // its own re-render, which re-reads history fresh for the sort header's
  // Agent Picks selector - no separate "update the menu" step needed.
  onAgentPicksChange((agentPickIds, meta) => {
    cntlDrawer?.setAttributeVisible('agentPicks', !!meta);
    if (map && cntlDrawer) {
      const attributes = cntlDrawer.getAttributes();
      map.setBadgeOptions({
        show: attributes,
        favoriteIds: getFavorites(),
        alertIds: getAlerts(),
        agentPickIds,
        featuredIds: getAgentFeatured(),
      });
    }
    listingsDrawer?.setAgentPicks(agentPickIds);
    if (cntlDrawer?.getAttributes().agentPicks) {
      applyFiltersToListings();
    }
  });

  // Subscribe to Featured changes (opening a fresh ?agent= link) - same
  // shape as Agent Picks above, but non-exclusive (setAttributeChecked,
  // not showOnlyAttribute - see utils/agentFeatured.ts's doc comment for
  // why) and no history/menu entry, since there's only ever one current
  // featured list per agent, nothing to browse back through.
  onAgentFeaturedChange((featuredIds, meta) => {
    cntlDrawer?.setAttributeVisible('featured', !!meta);
    if (map && cntlDrawer) {
      const attributes = cntlDrawer.getAttributes();
      map.setBadgeOptions({
        show: attributes,
        favoriteIds: getFavorites(),
        alertIds: getAlerts(),
        agentPickIds: getAgentPicks(),
        featuredIds,
      });
    }
    listingsDrawer?.setFeatured(featuredIds);
    if (cntlDrawer?.getAttributes().featured) {
      applyFiltersToListings();
    }
  });

  // Initialize UI components first (shows immediately, doesn't need map)
  initFilterDrawer();
  initDisplayDrawer();
  initUnifiedDrawer();
  initPropertyModal();
  initNeighborhoodModal();
  initGeographyModal();
  initHeader();
  void loadSiteAgentBranding();
  void initPersistentHeader({ api, subdomain: getSubdomain() });

  // Start map initialization in background (don't block on it)
  const mapPromise = initMap();

  // Kick off the drawer's richer (slower, mls_listings-direct) data in the
  // background - deliberately NOT part of the Promise.all below, so it can
  // never delay first bubbles. The drawer shows its own loading spinner
  // (via applyFiltersToListings) until this resolves independently.
  void loadListingsForDrawer();

  // Load neighborhoods, map markers (fast tmk_status path), and region/
  // district labels in parallel (doesn't need map)
  await Promise.all([
    loadNeighborhoodsData(),
    loadMapMarkers(),
    loadGeographyData(),
  ]);

  // Update listings drawer immediately (doesn't need map)
  applyFiltersToListings();

  // Wait for map to finish initializing
  await mapPromise;

  // Now that map is ready, update it with data
  if (map) {
    map.setNeighborhoods(neighborhoods); // lightweight/summary - instant bubbles, no polygons yet
    map.setCounties(counties);
    map.setRegions(regions);
    map.setDistricts(districts);
    // Re-apply to update map with listings
    applyFiltersToListings();
  }

  // Kick off the full (polygon-bearing) load in the background - not
  // awaited here, so it never delays first paint. See
  // ensureFullNeighborhoodsLoaded() for how Neighborhood Mode entry paths
  // avoid racing ahead of it.
  fullNeighborhoodsLoaded = loadFullNeighborhoodPolygons();

  // In embed mode, skip the router + search entirely. The router's home-route
  // handler calls exitNeighborhoodMode synchronously when the URL path is `/`
  // (which is always the case for embed URLs `/?embed=1&nh=...`), which would
  // immediately undo the enterNeighborhoodMode call below. We don't need
  // routing or search inside the iframe — every click navigates to the top
  // window via the capture-phase handler set up earlier.
  if (embedParams.embed && embedParams.scope && map) {
    await applyDeepLinkScope(embedParams.scope);
    // Drop a transparent shield over the map. It swallows every pointer event
    // (click, drag, wheel, touch) and navigates the parent window. Map below
    // can render its tiles + markers but nothing reaches its handlers — no
    // pan/zoom, no marker click. Cleaner than fiddling with map options.
    installEmbedClickShield();
    return;
  }

  // Normal (non-embed) startup: router + search.
  AccountPage.init(api);
  initRouter({
    onNeighborhoodRoute: handleNeighborhoodRoute,
    onListingRoute: handleListingRoute,
    onPropertyRoute: handlePropertyRoute,
    onAccountRoute: () => AccountPage.show(),
    onHomeRoute: () => { AccountPage.hide(); handleHomeRoute(); },
  });

  setupSearch();

  // Outside of embed mode, process the deep-link captured at the top of init.
  // (We can't re-read window.location.search here because router's home-route
  // + modal.close path may have stripped the query string already.)
  const { scope, propertyMlsId, tab, auth } = initialDeepLink;
  console.log('[deep-link] initial captured scope=', scope, 'p=', propertyMlsId, 'tab=', tab);

  if (propertyMlsId) {
    void openListingFromQuery(propertyMlsId);
  } else if (scope?.type === 'neighborhood' && tab === 'activity' && map) {
    void openNeighborhoodActivityFromDeepLink(scope.slug);
  } else if (scope && map) {
    void applyDeepLinkScope(scope);
  }

  // "Login" hand-off from a static agent custom page's nav - see
  // initialDeepLink's `auth` field above.
  if (auth) {
    resetAuthIntroCopy();
    document.getElementById('auth-modal')?.classList.remove('hidden');
  }

  // `?story=<token>` (Agent Picks): parallel to the deep-link handling
  // above, not part of DeepLinkScope, since it resolves to a whole set of
  // items rather than one entity. Auto-enables the "Agent Picks" attribute
  // so the recipient immediately sees their agent's list, not every listing.
  void checkUrlForStory(api).then((meta) => {
    if (!meta) return;
    cntlDrawer?.showOnlyAttribute('agentPicks');
    const who = meta.agentName ? `${meta.agentName}'s` : "Your agent's";
    const count = meta.itemCount === 1 ? '1 pick' : `${meta.itemCount} picks`;
    showToast(`${who} list is ready — showing ${count}`);
  });

  // `?agent=<slug>` (Featured): parallel to `?story=` above, but additive
  // (setAttributeChecked, not showOnlyAttribute) rather than isolating the
  // view - see utils/agentFeatured.ts's doc comment for why.
  void checkUrlForAgent(api).then((meta) => {
    if (!meta) return;
    cntlDrawer?.setAttributeChecked('featured', true);
    const who = meta.agentName ? `${meta.agentName}'s` : "This agent's";
    const count = meta.itemCount === 1 ? '1 pick' : `${meta.itemCount} picks`;
    showToast(`${who} featured picks are on — showing ${count}`);
  });
}

/**
 * Deep-link from `?p=<mlsId>`: enter neighborhood mode for the listing's
 * neighborhood (if visible), then open the PropertyModal for that listing.
 *
 * Pulls listing data from the cached `listings` array first; falls back to
 * data-api `/listings/:mlsId` if the listing isn't in the current snapshot
 * (e.g. it's Sold or filtered out).
 */
async function openListingFromQuery(mlsId: string): Promise<void> {
  console.log('[?p=] start, mlsId:', mlsId, 'map:', !!map, 'propertyModal:', !!propertyModal, 'cached listings:', listings.length);
  if (!map || !propertyModal) {
    console.warn('[?p=] map or propertyModal not ready, abort');
    return;
  }

  let neighborhoodId: string | undefined;
  const cached = listings.find((l) => l.listingId === mlsId);

  if (cached) {
    console.log('[?p=] found in cached listings, neighborhoodId:', cached.neighborhoodId);
    neighborhoodId = cached.neighborhoodId;
  } else {
    console.log('[?p=] not in cache, fetching from data-api...');
    try {
      const full = await api.getListing(mlsId);
      neighborhoodId = (full as Record<string, unknown>).neighborhoodId as string | undefined;
      console.log('[?p=] fetched, neighborhoodId:', neighborhoodId);
    } catch (err) {
      console.warn(`[?p=] Could not look up listing ${mlsId}:`, err);
    }
  }

  // Enter NHM for the listing's neighborhood, if it's visible to the public.
  if (neighborhoodId) {
    await ensureFullNeighborhoodsLoaded();
    const nh = neighborhoods.find((n) => n.id === neighborhoodId);
    if (nh) {
      console.log('[?p=] entering NHM for', nh.slug);
      map.enterNeighborhoodMode(nh.id);
      currentNeighborhoodMode = { id: nh.id, name: nh.name };
      applyFiltersToListings();
    } else {
      console.warn('[?p=] neighborhood not in visible list:', neighborhoodId);
    }
  }

  // Open the modal. showListingModal reads cached listings (which works for
  // Active in the current filter); for anything else (Sold, Pending, filtered
  // out) the modal opens nearly empty, so always trigger the full MLS detail
  // fetch to populate it.
  try {
    console.log('[?p=] opening property modal');
    await showListingModal(mlsId);
    console.log('[?p=] modal open() returned, fetching MLS detail');
    void loadMlsDataForModal(mlsId);
  } catch (err) {
    console.error('[?p=] modal open failed:', err);
  }
}

/**
 * Initialize the map
 *
 * Note: map variable is only set after initialization completes
 * to ensure map methods aren't called on an uninitialized map.
 */
async function initMap(): Promise<void> {
  const mapContainer = document.getElementById('map');
  if (!mapContainer) return;

  const newMap = new SlowMauiMap({
    container: mapContainer,
    onNeighborhoodClick: handleNeighborhoodMapClick,
    onNeighborhoodModeExit: handleNeighborhoodModeExit,
    onFloatingLabelClick: handleFloatingLabelClick,
    onCountyClick: handleCountyMapClick,
    onRegionClick: handleRegionMapClick,
    onDistrictClick: handleDistrictMapClick,
    onListingClick: handleListingClick,
    onTmkStatusMarkerClick: handleTmkStatusMarkerClick,
    onLoadBuildings: loadBuildingsForNeighborhood,
    // NEW: Use tmk_status endpoint for pre-computed markers
    onLoadMarkers: loadMarkers,
    onBoundsChanged: handleBoundsChanged,
    onMapReady: () => {
      console.log('Map ready');
    },
  });

  await newMap.initialize();
  map = newMap; // Only set after fully initialized
  newMap.setBuildingExtrusionEnabled(FEATURE_FLAGS.buildingExtrusion);
  newMap.setOffMarketUnitLabelsEnabled(FEATURE_FLAGS.offMarketUnitLabels);

  // initUnifiedDrawer() runs before initMap() and auto-opens the drawer on
  // desktop - its onOpen fires with `map` still null at that point, so
  // setRightInset() silently no-ops and a fit-bounds call (e.g. a
  // neighborhood search's enterNeighborhoodMode) never reserves space for
  // an already-open drawer until the user manually toggles it. Sync once
  // here, now that `map` actually exists.
  newMap.setRightInset(unifiedDrawer?.isOpen() ? unifiedDrawer.getWidth() : 0);
}

/**
 * Handle map bounds change - update viewport filtering for listings drawer
 */
function handleBoundsChanged(bounds: { north: number; south: number; east: number; west: number }): void {
  currentViewportBounds = bounds;

  // Ask mode shows an exact, fixed result set - a viewport-driven refetch/
  // re-filter of the regular listings on every pan/zoom would silently
  // overwrite what Ask explicitly put on the map and in the sidebar.
  if (askModeActive) return;

  // scheduleViewportReload (via getListingsFetchBounds) already re-derives
  // whether the current zoom needs real bounds or not, and canReuseCache
  // already knows an unbounded fetch covers every viewport - no need to
  // special-case "zoomed out" here by clearing state, which used to force a
  // fresh island-wide re-fetch on every single pan at low zoom (see
  // lastFetchWasUnbounded's comment).
  scheduleViewportReload();
}

/**
 * Debounced re-fetch of listings for the current viewport. handleBoundsChanged
 * fires on every map 'idle' (already coalesces continuous dragging into one
 * event per settled gesture), but a user doing several quick separate pans
 * in a row would otherwise fire one network request each - this collapses
 * those into a single fetch after panning actually stops.
 *
 * If the new viewport is still fully within the padded region we already
 * fetched (and that data isn't stale), the in-memory `mapMarkerListings`/
 * `listings` sets already cover this pan - just re-render from them instead
 * of hitting the network again. Otherwise falls through to a real re-fetch
 * via reloadListings().
 */
function scheduleViewportReload(): void {
  if (viewportReloadTimer) clearTimeout(viewportReloadTimer);
  viewportReloadTimer = setTimeout(() => {
    viewportReloadTimer = null;

    const fetchBounds = getListingsFetchBounds();
    const cacheFresh = Date.now() - lastFetchedListingsAt < LISTINGS_CACHE_MAX_AGE_MS;
    const canReuseCache = cacheFresh && (
      fetchBounds
        // Zoomed in with real bounds - only reusable if the padded region we
        // already fetched still contains the new viewport.
        ? !lastFetchWasUnbounded &&
          !!lastFetchedListingsBounds &&
          !!currentViewportBounds &&
          boundsContain(lastFetchedListingsBounds, currentViewportBounds)
        // Zoomed out (or Favorites/Alerts active) - island-wide data is
        // needed, and an already-loaded island-wide set covers any pan.
        : lastFetchWasUnbounded
    );

    if (canReuseCache) {
      applyFiltersToListings();
    } else {
      reloadListings();
    }
  }, VIEWPORT_RELOAD_DEBOUNCE_MS);
}

/**
 * Check if a listing is within the current viewport bounds
 */
function isListingInViewport(listing: ListingSummary): boolean {
  if (!currentViewportBounds) return true;
  if (!listing.lat || !listing.lng) return true; // Include listings without coords

  const { north, south, east, west } = currentViewportBounds;
  return (
    listing.lat <= north &&
    listing.lat >= south &&
    listing.lng <= east &&
    listing.lng >= west
  );
}

/**
 * Initialize the filter drawer (top)
 */
function initFilterDrawer(): void {
  const container = document.getElementById('filter-drawer');
  if (!container) return;

  filterDrawer = new FilterDrawer({
    container,
    onFilterChange: handleFilterChange,
    onToggle: handleFiltersToggle,
    onAskToggle: handleAskModeToggle,
  });
}

/**
 * Handle filter changes from filter drawer
 */
async function handleFilterChange(filters: PropertyFilters): Promise<void> {
  const keywordChanged = filters.keyword !== currentFilters.keyword;
  currentFilters = filters;

  if (keywordChanged) {
    await updateKeywordMatches(filters.keyword);
  }

  // Re-filter the displayed listings based on filters
  applyFiltersToListings();
}

/**
 * Resolves the Keyword filter's match set from data-api. Fails open (clears
 * the restriction rather than hiding everything) on a network error - a
 * transient failure on a text filter shouldn't read as "zero results."
 */
async function updateKeywordMatches(keyword: string | undefined): Promise<void> {
  if (!keyword) {
    keywordMatchTmks = null;
    return;
  }
  try {
    const status = computeStatusesToLoad().join(',');
    const tmks = await api.searchListingIds(keyword, status || undefined);
    keywordMatchTmks = new Set(tmks);
  } catch (error) {
    console.error('Keyword search failed:', error);
    keywordMatchTmks = null;
  }
}

/**
 * Apply current filters to listings and update map/drawer
 *
 * Visibility Logic: A property is visible if it matches ANY selected Status
 * OR matches ANY selected Attribute.
 *
 * - If For Sale ON + New ON: All For Sale properties show + New properties show (badges on New ones)
 * - If For Sale OFF + New ON: Only New properties show
 * - Attributes add badges, but also make properties visible even if status doesn't match
 */
/**
 * The full status/attribute/viewport/price/beds/baths/type/STR filter +
 * sort pipeline, extracted so it can run independently against the map's
 * fast tmk_status-derived data and the drawer's slower mls_listings-derived
 * data - see mapMarkerListings/loadListingsForDrawer above. Identical logic
 * either way; only the source array differs.
 */
function filterAndSortListings(source: ListingSummary[]): ListingSummary[] {
  if (!cntlDrawer) return source;

  const statuses = cntlDrawer.getStatuses();
  const attributes = cntlDrawer.getAttributes();

  // Build status match set
  const selectedStatuses = new Set<string>();
  if (statuses.forSale) selectedStatuses.add('Active');
  if (statuses.pending) selectedStatuses.add('Pending');
  if (statuses.recentlySold) selectedStatuses.add('Sold');
  if (statuses.offMarket) {
    selectedStatuses.add('Cancelled');
    selectedStatuses.add('Withdrawn');
    selectedStatuses.add('Expired');
  }

  // Attribute check helpers
  const favoriteIds = getFavorites();
  const alertIds = getAlerts();
  const agentPickIds = getAgentPicks();
  const featuredIds = getAgentFeatured();
  const idSets: AttributeIdSets = { favorites: favoriteIds, alerts: alertIds, agentPicks: agentPickIds, featured: featuredIds };

  let filtered = source.filter((listing) => {
    // Check if matches any selected status
    const matchesStatus = selectedStatuses.has(listing.status);

    // Check if matches any selected attribute (Status OR Attribute visibility)
    const facts = factsFromListingSummary(listing);
    const matchesAttribute = ATTRIBUTE_REGISTRY.some(
      (entry) => attributes[entry.id] && entry.matches(facts, idSets)
    );

    // Visible if matches status OR matches attribute
    return matchesStatus || matchesAttribute;
  });

  // Apply viewport filter when zoomed in
  const currentZoom = map?.getZoom() ?? 0;
  if (currentViewportBounds && currentZoom >= VIEWPORT_FILTER_MIN_ZOOM) {
    filtered = filtered.filter(isListingInViewport);
  }

  // Apply price/beds/baths/type filters only if filters are enabled
  if (filtersEnabled) {
    if (currentFilters.minPrice !== undefined) {
      filtered = filtered.filter((l) => l.listPrice >= currentFilters.minPrice!);
    }
    if (currentFilters.maxPrice !== undefined) {
      filtered = filtered.filter((l) => l.listPrice <= currentFilters.maxPrice!);
    }
    if (currentFilters.minBedrooms !== undefined) {
      filtered = filtered.filter((l) => (l.bedrooms || 0) >= currentFilters.minBedrooms!);
    }
    if (currentFilters.minBathrooms !== undefined) {
      filtered = filtered.filter((l) => (l.bathrooms || 0) >= currentFilters.minBathrooms!);
    }
    if (currentFilters.minYearBuilt !== undefined) {
      filtered = filtered.filter((l) => (l.yearBuilt || 0) >= currentFilters.minYearBuilt!);
    }
    if (currentFilters.minSqft !== undefined) {
      filtered = filtered.filter((l) => (l.livingArea || 0) >= currentFilters.minSqft!);
    }
    if (currentFilters.maxSqft !== undefined) {
      filtered = filtered.filter((l) => l.livingArea !== undefined && l.livingArea <= currentFilters.maxSqft!);
    }
    if (currentFilters.minDaysOnMarket !== undefined) {
      filtered = filtered.filter((l) => (l.daysOnMarket || 0) >= currentFilters.minDaysOnMarket!);
    }
    if (currentFilters.keyword && keywordMatchTmks) {
      filtered = filtered.filter((l) => l.tmkNormalized !== undefined && keywordMatchTmks!.has(l.tmkNormalized));
    }
    // Property type filter - only apply if some types are deselected (not all 3)
    const allTypes = ['house', 'condo', 'land'];
    const selectedTypes = currentFilters.propertyTypes || [];
    const typeFilterActive = selectedTypes.length > 0 && selectedTypes.length < allTypes.length;

    if (typeFilterActive) {
      filtered = filtered.filter((l) => {
        const type = (l.propertyType || '').toLowerCase();
        // Map MLS PropertyType values to our filter categories:
        // - "Residential" -> house (single family, multi family, etc.)
        // - "Condominium" -> condo
        // - "Land" -> land
        if (selectedTypes.includes('house') && type === 'residential') {
          return true;
        }
        if (selectedTypes.includes('condo') && type === 'condominium') {
          return true;
        }
        if (selectedTypes.includes('land') && type === 'land') {
          return true;
        }
        return false;
      });
    }

    // STR (short-term rental) filter, via zoning - ignores the Minatoya
    // exemption for now (per Todd), so Apartment-zoned listings are always
    // treated as long-term-only rather than checking the grandfathered
    // list. Listings with no zoning on file are "unknown," not "no" -
    // they're excluded from both Yes and No (only visible under All),
    // since asserting a false negative (telling someone a property isn't
    // STR-eligible when we simply don't have data) is worse than omitting
    // it from a narrowed search.
    if (currentFilters.strAllowed === 'yes' || currentFilters.strAllowed === 'no') {
      filtered = filtered.filter((l) => {
        const { category, strAllowed } = getZoningCategory(l.zoning);
        // No zoning value, or a code the classifier doesn't recognize
        // (e.g. B-2/M-1/AG - not part of the Residential/Apartment/Hotel
        // framework at all) - genuinely unknown, not a confirmed "no."
        if (category === null) return false;
        return currentFilters.strAllowed === 'yes' ? strAllowed : !strAllowed;
      });
    }
  }

  // Sort listings based on active attributes (attributes at top with badges)
  return sortListings(filtered, { attributes, favoriteIds, alertIds, agentPickIds, featuredIds });
}

/**
 * Apply current filters to listings and update map/drawer
 *
 * Visibility Logic: A property is visible if it matches ANY selected Status
 * OR matches ANY selected Attribute.
 *
 * - If For Sale ON + New ON: All For Sale properties show + New properties show (badges on New ones)
 * - If For Sale OFF + New ON: Only New properties show
 * - Attributes add badges, but also make properties visible even if status doesn't match
 *
 * Map and drawer are populated from two INDEPENDENT sources (see
 * mapMarkerListings/loadListingsForDrawer above) so the map never waits on
 * the drawer's richer, slower data. Ask mode is the one exception - its
 * `listings` array IS the exact answer set Ask's own structured/semantic/
 * freshness query already produced server-side, and both map and drawer
 * show it directly (re-running the CNTL visibility rule on top of it would
 * incorrectly hide results whose status doesn't match today's separate
 * CNTL toggle selection; Ask has its own status handling already).
 */
function applyFiltersToListings(): void {
  if (!listingsDrawer || !cntlDrawer) return;

  if (askModeActive) {
    listingsDrawer.setLoading(false);
    listingsDrawer.setListings(listings);
    if (map) {
      map.setPropertyFilters({});
      map.setListings(listings);
    }
    return;
  }

  // Map: always from the fast path, updates as soon as it's loaded.
  const sortedForMap = filterAndSortListings(mapMarkerListings);
  if (map) {
    if (filtersEnabled) {
      map.setPropertyFilters({
        minPrice: currentFilters.minPrice,
        maxPrice: currentFilters.maxPrice,
        minBedrooms: currentFilters.minBedrooms,
        minBathrooms: currentFilters.minBathrooms,
        propertyTypes: currentFilters.propertyTypes,
        minYearBuilt: currentFilters.minYearBuilt,
        minSqft: currentFilters.minSqft,
        maxSqft: currentFilters.maxSqft,
        minDaysOnMarket: currentFilters.minDaysOnMarket,
      });
    } else {
      map.setPropertyFilters({});
    }
    map.setListings(sortedForMap);
  }

  // Drawer: only from the richer slow-path data once it's actually loaded -
  // shows its own loading state independently until then, rather than
  // either blocking on it or showing the map's lighter data.
  if (drawerListingsReady) {
    listingsDrawer.setLoading(false);
    // In Neighborhood Mode, scope the drawer to just that neighborhood -
    // this function is the one place every "enter NHM" call site's refresh
    // eventually funnels through (any pan/zoom settle calls it via
    // reloadListings), so handling the scoping HERE covers all of them,
    // not just handleNeighborhoodMapClick's own direct-click path (which
    // duplicates this filter inline for instant feedback, but was the
    // ONLY one of five "enter NHM" call sites that did - the other four,
    // e.g. NeighborhoodModal's "View on Map" button, silently left the
    // drawer unscoped until a later map event happened to trigger this
    // function and coincidentally look right because the viewport had
    // narrowed to just that neighborhood by then).
    const drawerListings = currentNeighborhoodMode
      ? listings.filter(l => l.neighborhoodId === currentNeighborhoodMode!.id)
      : listings;
    listingsDrawer.setListings(filterAndSortListings(drawerListings));
  } else {
    listingsDrawer.setLoading(true);
  }
}

/**
 * Initialize the display drawer (bottom - non-listing controls)
 */
function initDisplayDrawer(): void {
  const container = document.getElementById('display-drawer');
  if (!container) return;

  displayDrawer = new DisplayDrawer({
    container,
    initialOptions: {
      satellite: false,
      overlay: 'none',
      neighborhoods: { labels: true, outline: false },
      // Labels default on - districts have always shown their name bubbles
      // (no toggle existed before this feature); Outline is the new part
      // and correctly starts off, same default it had before Labels split
      // out as its own concept.
      districts: { labels: true, outline: false },
    },
    onDisplayChange: handleDisplayChange,
    onMyLocation: handleMyLocation,
  });

  const mapContainer = document.getElementById('map-container');
  if (mapContainer) {
    // Zoning legend (shows when Neighborhood fill = Zoning)
    zoningLegend = new ZoningLegend({
      container: mapContainer,
      onCategoryToggle: (category, visible) => {
        map?.setZoningCategoryVisible(category, visible);
      },
    });

    // Market Trends legend (shows when Overlay = Trends) - always shows the
    // neighborhood range (see applyPriceHeatmapRangeToLegend), even though
    // districts are colored simultaneously.
    marketTrendsLegend = new MarketTrendsLegend({
      container: mapContainer,
      onMetricChange: handleMarketTrendsMetricChange,
    });

    // Climate legend (shows when Overlay = Climate) - same neighborhood-range
    // convention as Market Trends above.
    climateLegend = new ClimateLegend({
      container: mapContainer,
      onDatatypeChange: handleClimateDatatypeChange,
    });
  }
}

/**
 * Handle display option changes (satellite, neighborhoods, districts).
 * Fires on every change, so forward on/off changes only when they actually flipped.
 */
function handleDisplayChange(options: DisplayOptions): void {
  if (!map) return;

  // Toggle satellite view
  if (options.satellite) {
    map.setMapType('hybrid');
  } else {
    map.setMapType('roadmap');
  }

  // Forward Labels changes to the existing handler (preserves NHM-exit on disable)
  if (options.neighborhoods.labels !== neighborhoodsEnabled) {
    handleNeighborhoodsToggle(options.neighborhoods.labels);
  }

  map.setNeighborhoodOutlineEnabled(options.neighborhoods.outline);
  map.setDistrictsEnabled(options.districts.labels);
  map.setDistrictOutlineEnabled(options.districts.outline);

  // Overlay is a single exclusive choice now (Off/Zoning/Trends/Climate),
  // neighborhood-only. District fill is disabled, not removed - districts
  // span too much ground for one fill color to mean anything (some run
  // mountain to sea), but setDistrictFillMode/applyDistrictFill/the
  // hole-punch logic all still exist in Map.ts if this is ever revisited.
  const neighborhoodFillMode: NeighborhoodFillMode =
    options.overlay === 'zoning' ? 'zoning'
    : options.overlay === 'trends' ? 'marketTrends'
    : options.overlay === 'climate' ? 'climate'
    : 'none';

  map.setNeighborhoodFillMode(neighborhoodFillMode);
  map.setDistrictFillMode('none');

  zoningLegend?.toggle(options.overlay === 'zoning');

  const marketTrendsActive = options.overlay === 'trends';
  if (marketTrendsActive) {
    void ensurePriceHeatmapLoaded().then(() => {
      applyPriceHeatmapRangeToLegend();
    });
  }
  marketTrendsLegend?.toggle(marketTrendsActive);

  const climateActive = options.overlay === 'climate';
  if (climateActive) {
    const datatype = climateLegend?.getDatatype() ?? 'rainfall';
    void ensureClimateDataLoaded(datatype).then(() => {
      applyClimateRangeToLegend();
    });
  }
  climateLegend?.toggle(climateActive);
}

/**
 * Fetches /stats/price-heatmap once and feeds it to the map - subsequent
 * calls (re-toggling Market Trends on/off) reuse the cached result rather
 * than refetching every time. Carries both selectable metrics; which one
 * actually renders is controlled separately via map.setPriceMetric.
 */
async function ensurePriceHeatmapLoaded(): Promise<void> {
  if (priceHeatmapData || priceHeatmapLoading) return;
  priceHeatmapLoading = true;
  try {
    priceHeatmapData = await api.getPriceHeatmap();
    map?.setPriceHeatmapData({
      medianSalePrice: priceHeatmapData.medianSalePrice,
      currentForSale: priceHeatmapData.currentForSale,
    });
  } catch (error) {
    console.error('Failed to load price heatmap data:', error);
  } finally {
    priceHeatmapLoading = false;
  }
}

// Trends now colors neighborhoods and districts simultaneously, so there's
// no single "active scope" anymore - the legend always shows the
// neighborhood range as the primary/detailed layer; district coloring is a
// supplementary signal not separately numbered in the legend.
function applyPriceHeatmapRangeToLegend(): void {
  if (!map || !marketTrendsLegend) return;
  marketTrendsLegend.setScope('neighborhoods');
  const range = map.getNeighborhoodPriceRange();
  marketTrendsLegend.setRange(range.min, range.center, range.max);
}

function handleMarketTrendsMetricChange(metric: PriceMetric): void {
  map?.setPriceMetric(metric);
  applyPriceHeatmapRangeToLegend();
}

// Same "neighborhood range is primary" convention as Market Trends above.
async function ensureClimateDataLoaded(datatype: ClimateDatatype): Promise<void> {
  if (climateDataLoaded.has(datatype)) return;
  climateDataLoaded.add(datatype);
  try {
    const [neighborhoodResult, districtResult] = await Promise.all([
      api.getClimateData({ entityType: 'neighborhood', datatype }),
      api.getClimateData({ entityType: 'district', datatype }),
    ]);
    map?.setClimateHeatmapData(datatype, {
      neighborhoods: Object.fromEntries(neighborhoodResult.values.map((v) => [v.entityId, { value: v.value }])),
      districts: Object.fromEntries(districtResult.values.map((v) => [v.entityId, { value: v.value }])),
    });
    climateSourceByDatatype[datatype] = neighborhoodResult.source;
    if (climateLegend?.getDatatype() === datatype) {
      applyClimateRangeToLegend();
    }
  } catch (error) {
    console.error(`Failed to load climate data (${datatype}):`, error);
    climateDataLoaded.delete(datatype);
  }
}

function applyClimateRangeToLegend(): void {
  if (!map || !climateLegend) return;
  climateLegend.setScope('neighborhoods');
  const range = map.getNeighborhoodClimateRange();
  climateLegend.setRange(range.min, range.center, range.max);
  const datatype = map.getClimateDatatype();
  const source = climateSourceByDatatype[datatype];
  climateLegend.setSource(source ? { name: source.name, url: source.url } : null);
}

function handleClimateDatatypeChange(datatype: ClimateDatatype): void {
  map?.setClimateDatatype(datatype);
  void ensureClimateDataLoaded(datatype).then(() => {
    applyClimateRangeToLegend();
  });
}

// Toggle state for My Location tracking - a second click while active turns
// it off, matching Google Maps' own button behavior (not a one-shot pan).
let myLocationWatchId: number | null = null;

/**
 * Handle My Location button - toggles continuous position tracking with a
 * Google-Maps-style blue-dot marker (see SlowMauiMap.setUserLocationMarker).
 * First fix pans/zooms the map once; subsequent updates while active only
 * move the dot, never the camera, so it doesn't fight a user who's since
 * panned away manually.
 */
function handleMyLocation(): void {
  if (!map || !navigator.geolocation) return;

  if (myLocationWatchId !== null) {
    navigator.geolocation.clearWatch(myLocationWatchId);
    myLocationWatchId = null;
    void map.setUserLocationMarker(null);
    displayDrawer?.setMyLocationActive(false);
    return;
  }

  let firstFix = true;
  myLocationWatchId = navigator.geolocation.watchPosition(
    (position) => {
      const { latitude, longitude, heading, accuracy } = position.coords;
      void map?.setUserLocationMarker({ lat: latitude, lng: longitude, heading, accuracy });

      if (firstFix) {
        firstFix = false;
        map?.panTo({ lat: latitude, lng: longitude });
        map?.setZoom(16);
        displayDrawer?.setMyLocationActive(true);
      }
    },
    (error) => {
      console.error('Geolocation error:', error);
      myLocationWatchId = null;
      displayDrawer?.setMyLocationActive(false);
      void map?.setUserLocationMarker(null);

      const message = error.code === error.PERMISSION_DENIED
        ? 'Location access was denied. Enable it in your browser settings to use My Location.'
        : 'Could not determine your location. Please try again.';
      showToast(message);
    },
    { enableHighAccuracy: true, maximumAge: 5000, timeout: 10000 }
  );
}

// Minimal toast, matching PropertyModal's own .sm-share-toast pattern/style
// (already injected into the page since PropertyModal is instantiated at
// startup) rather than a jarring alert() for a non-blocking notice. Optional
// `action` renders an inline button (e.g. "Sign In" after an anonymous
// alert toggle) - clicking it fires the callback and dismisses the toast
// immediately rather than waiting out the auto-dismiss timer.
function showToast(message: string, action?: { label: string; onClick: () => void }): void {
  const toast = document.createElement('div');
  toast.className = 'sm-share-toast';

  let dismissed = false;
  const dismiss = () => {
    if (dismissed) return;
    dismissed = true;
    toast.classList.remove('show');
    setTimeout(() => toast.remove(), 300);
  };

  if (action) {
    const content = document.createElement('div');
    content.className = 'sm-share-toast-content';
    const text = document.createElement('span');
    text.textContent = message;
    const actionBtn = document.createElement('button');
    actionBtn.type = 'button';
    actionBtn.className = 'sm-toast-action';
    actionBtn.textContent = action.label;
    actionBtn.addEventListener('click', () => {
      dismiss();
      action.onClick();
    });
    content.appendChild(text);
    content.appendChild(actionBtn);
    toast.appendChild(content);
  } else {
    toast.textContent = message;
  }

  document.body.appendChild(toast);

  requestAnimationFrame(() => toast.classList.add('show'));
  setTimeout(dismiss, 4000);
}

function openAuthModal(): void {
  document.getElementById('auth-modal')?.classList.remove('hidden');
}

// Default copy for the auth modal's generic entry points (header menu,
// etc). requestAlertSignIn swaps this for a contextual line and
// resetAuthIntroCopy() restores it once there's no pending alert.
const DEFAULT_AUTH_INTRO = 'Sign in to save favorites and get alerts for new listings.';

function resetAuthIntroCopy(): void {
  const introEl = document.getElementById('authSignInIntro');
  if (introEl) introEl.textContent = DEFAULT_AUTH_INTRO;
}

// The single alert the user most recently tried to set while logged out -
// see requestAlertSignIn/applyPendingAlertIfAny. Only one at a time since
// the auth modal blocks further interaction until it's resolved.
let pendingAlertKey: string | null = null;

/**
 * Alerts require login (see utils/alerts.ts) - clicking a bell while
 * logged out no longer flips it or sets anything; it opens the auth modal
 * with a line explaining what signing in unlocks, and remembers the exact
 * item so applyPendingAlertIfAny() can finish the job the moment sign-in
 * completes, without the user having to find the bell and click it again.
 */
function requestAlertSignIn(key: string, contextLabel: string): void {
  pendingAlertKey = key;
  const introEl = document.getElementById('authSignInIntro');
  if (introEl) introEl.textContent = `Sign in to get notified about ${contextLabel}.`;
  openAuthModal();
}

/**
 * Applies whatever alert the user attempted before being gated to sign in.
 * Called from syncUserWithApi() - specifically after initAlertsForUser()
 * has run, not from a separate onAuthStateChange listener, since
 * alerts.ts's toggleAlert() throws until that call has actually happened;
 * a sibling listener firing concurrently with (rather than after)
 * syncUserWithApi could otherwise race it.
 */
async function applyPendingAlertIfAny(): Promise<void> {
  if (!pendingAlertKey) return;
  const key = pendingAlertKey;
  pendingAlertKey = null;
  try {
    const newState = await toggleAlert(key);
    refreshOpenModalAlertState(key, newState);
  } catch (error) {
    console.error('Failed to apply pending alert after sign-in:', error);
  }
}

/**
 * If the modal/surface that triggered requestAlertSignIn is still open,
 * flip its bell in place rather than leaving it stale until the next time
 * the user opens it. Each modal's setAlertState() no-ops on its own if it's
 * since moved on to a different item.
 */
function refreshOpenModalAlertState(key: string, isAlerted: boolean): void {
  if (key.startsWith('nh:')) {
    neighborhoodModal?.setAlertState(key.substring(3), isAlerted);
  } else if (key.startsWith('d:') || key.startsWith('r:')) {
    const entityType = key.startsWith('r:') ? 'region' : 'district';
    geographyModal?.setAlertState(key.substring(2), entityType, isAlerted);
  } else {
    propertyModal?.setAlertState(key, isAlerted);
  }
}

/**
 * Initialize the unified drawer: a single right-side drawer with one handle and
 * a panel split into two stacked sections (CNTL controls on top, Listings below).
 * Replaces the previous coordinated CNTLDrawer + ListingsDrawer pair.
 */
function initUnifiedDrawer(): void {
  const container = document.getElementById('unified-drawer');
  if (!container) return;

  unifiedDrawer = new UnifiedDrawer({
    container,
    initialWidth: 360,
    // A collapsed neighborhood group's preview card count is computed from
    // the drawer's current width (see ListingsDrawer.computeColumnCount) -
    // opening or resizing changes that width without otherwise touching
    // the listings data, so nothing would normally trigger a re-render to
    // pick up the new count.
    onOpen: () => {
      listingsDrawer?.refreshLayout();
      map?.setRightInset(unifiedDrawer?.getWidth() ?? 0);
    },
    onClose: () => map?.setRightInset(0),
    onResize: (width) => {
      listingsDrawer?.refreshLayout();
      if (unifiedDrawer?.isOpen()) map?.setRightInset(width);
    },
  });

  const initialStatuses = { forSale: true, pending: false, recentlySold: false, offMarket: false };

  cntlDrawer = new CNTLDrawer({
    container: unifiedDrawer.getControlsSlot(),
    initialStatuses,
    // Agent Picks/Featured only mean anything while active (restored from
    // localStorage by initAgentPicks()/initAgentFeatured(), called earlier
    // in init()) - hidden otherwise, same as Agent Picks disappears again
    // once it expires.
    initiallyHiddenAttributes: [
      ...(getActiveStoryMeta() ? [] : ['agentPicks' as const]),
      ...(getActiveFeaturedMeta() ? [] : ['featured' as const]),
    ],
    onStatusChange: handleStatusChange,
    onAttributeChange: handleAttributeChange,
    onHandleStateChange: ({ statuses, attributes }) => {
      unifiedDrawer?.setHandleDots({
        forSale: statuses.forSale,
        pending: statuses.pending,
        recentlySold: statuses.recentlySold,
        offMarket: statuses.offMarket,
      });
      unifiedDrawer?.setHandleIcons(attributes);
    },
  });

  listingsDrawer = new ListingsDrawer({
    container: unifiedDrawer.getListingsSlot(),
    groupByNeighborhood: true,
    favoriteIds: getFavorites(),
    alertIds: getAlerts(),
    agentPickIds: getAgentPicks(),
    featuredIds: getAgentFeatured(),
    // Replaces the old separate "Past Agent Picks" modal/hamburger entry -
    // switching lists now happens in context, right where the listings
    // themselves are. getHistory() is called fresh on every drawer render
    // (see setAgentPicks()'s updateBody() call, triggered by
    // onAgentPicksChange below), so a newly-opened `?story=` link shows up
    // here without any extra wiring.
    agentPicksMenu: {
      getHistory: () => getAgentPickHistory().map((h) => ({
        token: h.token,
        label: h.title || (h.agentName ? `${h.agentName}'s Picks` : 'Untitled Picks'),
        isActive: h.isActive,
      })),
      onSelect: (token) => {
        if (!token) {
          // "Current View" - just uncheck the attribute, don't touch
          // status filters or anything else already selected.
          cntlDrawer?.setAttributeChecked('agentPicks', false);
          return;
        }
        const meta = activateStory(token);
        if (!meta) return;
        cntlDrawer?.showOnlyAttribute('agentPicks');
        const who = meta.agentName ? `${meta.agentName}'s` : "Your agent's";
        showToast(`Showing ${who} list from ${formatRelativeDate(meta.loadedAt.slice(0, 10)).toLowerCase()}`);
      },
    },
    onListingClick: handleSidebarListingClick,
    onListingHover: (listing) => {
      if (map && listing) {
        map.highlightListing(listingItemKey(listing));
      } else if (map) {
        map.highlightListing(null);
      }
    },
    onFavoriteToggle: async (listingId) => {
      try {
        await toggleFavorite(listingId);
      } catch (error) {
        console.error('Failed to toggle favorite:', error);
      }
    },
    onNeighborhoodFavoriteToggle: async (neighborhoodId) => {
      try {
        await toggleFavorite(`nh:${neighborhoodId}`);
      } catch (error) {
        console.error('Failed to toggle neighborhood favorite:', error);
      }
    },
    onListingAlertToggle: async (listingId) => {
      if (!canCreateAlerts()) {
        const listing = listings.find(l => l.tmkNormalized === listingId || l.id === listingId);
        requestAlertSignIn(listingId, listing ? `updates on ${formatListingAddress(listing)}` : 'updates on this property');
        return;
      }
      try {
        await toggleAlert(listingId);
      } catch (error) {
        console.error('Failed to toggle listing alert:', error);
      }
    },
    onNeighborhoodAlertToggle: async (neighborhoodId) => {
      if (!canCreateAlerts()) {
        const nh = neighborhoods.find(n => n.id === neighborhoodId);
        requestAlertSignIn(`nh:${neighborhoodId}`, nh ? `new listings in ${formatNeighborhoodName(nh.name)}` : 'new listings here');
        return;
      }
      try {
        await toggleAlert(`nh:${neighborhoodId}`);
      } catch (error) {
        console.error('Failed to toggle neighborhood alert:', error);
      }
    },
    onNeighborhoodHeaderClick: handleNeighborhoodHeaderClick,
    onAddToCalendar: (listing) => {
      const ics = buildOpenHouseIcs(listing);
      if (!ics) return;
      const fileName = `open-house-${listing.listingId || listing.id}.ics`;
      downloadFile(fileName, ics, 'text/calendar;charset=utf-8');
    },
  });

  // Sync map's active statuses with CNTL initial state
  if (map) {
    const activeStatuses: string[] = [];
    if (initialStatuses.forSale) activeStatuses.push('Active');
    if (initialStatuses.pending) activeStatuses.push('Pending');
    if (initialStatuses.recentlySold) activeStatuses.push('Sold');
    if (initialStatuses.offMarket) activeStatuses.push('OffMarket');
    map.setActiveStatuses(activeStatuses);
  }

  // Default-open on desktop; closed on mobile (matches drawer CSS breakpoint).
  if (window.innerWidth >= 768) {
    unifiedDrawer.open();
  }
}

// NHM uses: Active→ForSale, Pending→Pending, Sold→RecentlySold, OffMarket→OffMarket
function cntlStatusesToMapStatuses(statuses: StatusFilters): string[] {
  const activeStatuses: string[] = [];
  if (statuses.forSale) activeStatuses.push('Active');
  if (statuses.pending) activeStatuses.push('Pending');
  if (statuses.recentlySold) activeStatuses.push('Sold');
  if (statuses.offMarket) activeStatuses.push('OffMarket');
  return activeStatuses;
}

/**
 * Handle status filter changes from CNTL drawer
 */
async function handleStatusChange(statuses: StatusFilters): Promise<void> {
  console.log(`[main.ts handleStatusChange] Called with:`, statuses);

  // Build active statuses list for the map
  const activeStatuses = cntlStatusesToMapStatuses(statuses);

  console.log(`[main.ts handleStatusChange] Calling map.setActiveStatuses([${activeStatuses.join(', ')}])`);

  // Update map's active statuses (enables fast toggling in NHM)
  if (map) {
    map.setActiveStatuses(activeStatuses);

    // If using NHM cache, skip API call - map already refreshed from cache
    if (map.isUsingNeighborhoodCache()) {
      console.log('[NHM] Status change handled via cache, skipping API call');
      // Still need to update the listings drawer
      applyFiltersToListings();
      return;
    }
  }

  // Not in NHM or no cache - load from API
  reloadListings();
}

/**
 * Handle attribute filter changes from CNTL drawer
 */
async function handleAttributeChange(attributes: AttributeFilters): Promise<void> {
  // Update map badge options
  if (map) {
    map.setBadgeOptions({
      show: attributes,
      favoriteIds: getFavorites(),
      alertIds: getAlerts(),
      agentPickIds: getAgentPicks(),
      featuredIds: getAgentFeatured(),
    });
  }

  // Pass active attributes to listings drawer for section rendering
  listingsDrawer?.setActiveAttributes(attributes);

  // Reload listings since we may need to load more data
  // (e.g., if "New" is on but "For Sale" is off, we need Active listings)
  reloadListings();

  console.log('Attributes changed:', attributes);
}

/**
 * Handle filters toggle change from Filter drawer
 */
function handleFiltersToggle(enabled: boolean): void {
  filtersEnabled = enabled;
  // Update listings drawer filter indicator
  listingsDrawer?.setFiltersActive(enabled);
  // Mutually exclusive with an active Ask result - turning regular filters
  // back on clears it, same as dismissing the Ask result banner directly.
  if (enabled && askModeActive) {
    filterDrawer?.setAskActive(false);
    clearAskModeState();
    // loadMapMarkers()/loadListingsForDrawer() both refuse to run while
    // askModeActive was true, so mapMarkerListings/listings are stale from
    // before Ask was entered - reloadListings() refetches both instead of
    // just re-rendering whatever's already there.
    reloadListings();
  } else {
    // Re-apply filters
    applyFiltersToListings();
  }
  console.log('Filters enabled:', enabled);
}

/**
 * Handle the Ask result toggle in the filter drawer. It only ever appears
 * once an Ask result is active and disappears the moment it's turned off,
 * so in practice this only ever fires with enabled=false (the "clear"
 * action) - never fires true since the toggle can't be switched back on
 * from the drawer itself without a new Ask query.
 */
function handleAskModeToggle(enabled: boolean): void {
  if (enabled) return;
  clearAskModeState();
  // See handleFiltersToggle's identical comment - mapMarkerListings/listings
  // are stale from before Ask was entered and need a real refetch.
  reloadListings();
}

/** Resets Ask-mode state and UI without re-applying the regular filters - callers do that themselves. */
function clearAskModeState(): void {
  askModeActive = false;
  askResultListings = [];
  askResultSummary = null;
  hideAskResultBanner();
  // activateAskMode() replaced the working `listings` array outright (so
  // applyFiltersToListings() renders Ask's exact set through the normal
  // pipeline) - restore whatever was there before, so the next
  // applyFiltersToListings() call goes back to the real, full dataset
  // instead of staying stuck on Ask's small subset.
  if (preAskListings) {
    listings = preAskListings;
    preAskListings = null;
  }
  map?.setNeighborhoodBadgesVisible(true);
  // Restore the map's own active-statuses filter to whatever the CNTL
  // toggles actually say - activateAskMode() had opened it to every status.
  if (cntlDrawer) {
    map?.setActiveStatuses(cntlStatusesToMapStatuses(cntlDrawer.getStatuses()));
  }
}

/**
 * Activates Ask mode: makes Ask's results the working `listings` set and
 * routes through the SAME applyFiltersToListings() pipeline every regular
 * filter change already uses (rather than poking map/drawer directly) -
 * that pipeline is what already correctly handles pan/zoom, viewport
 * culling, and neighborhood-mode interactions for the regular Filters
 * drawer, so Ask gets that behavior for free instead of needing its own
 * parallel bug-prone plumbing. applyFiltersToListings() skips the regular
 * CNTL status/attribute/price/bed/bath/type filters while askModeActive -
 * Ask's own query already encodes the desired result set.
 */
function activateAskMode(resultListings: ListingSummary[], summary: string | null): void {
  if (!askModeActive) {
    // Only snapshot on the *first* activation - a second Ask query run
    // while already in Ask mode must not overwrite this with the first
    // query's (already-narrowed) results.
    preAskListings = listings;
  }
  askModeActive = true;
  askResultListings = resultListings;
  askResultSummary = summary;
  listings = resultListings;

  filtersEnabled = false;
  filterDrawer?.setFiltersOn(false);
  listingsDrawer?.setFiltersActive(false);
  filterDrawer?.setAskActive(true);

  // Neighborhood mode renders its own wider per-neighborhood marker set
  // (every status, not just `listings`) whenever active, which would
  // otherwise swamp Ask's specific result set with every other property in
  // the neighborhood - NHM simply can't represent an arbitrary exact
  // result set, so it's never compatible with Ask. Exit unconditionally
  // (a no-op if not already in neighborhood mode); a click on a
  // neighborhood's own label while Ask is active exits Ask mode instead
  // (see handleNeighborhoodMapClick), same mutual-exclusivity pattern as
  // the regular filters toggle.
  map?.exitNeighborhoodMode();
  currentNeighborhoodMode = null;

  // A separate legacy layer - static per-neighborhood "for-sale count"
  // badges, populated once at startup from each neighborhood's own
  // forSaleCount (totally unrelated to whatever's passed to setListings())
  // - visually near-identical to real Active markers and gated purely by
  // zoom, independent of neighborhood mode/visibility. fitMapToPoints()
  // below often zooms out far enough (island-wide results) to land right
  // in that layer's visible zoom band, so it must be force-hidden
  // explicitly while Ask is active, not just left to neighborhood-mode exit.
  map?.setNeighborhoodBadgesVisible(false);

  // Map.ts's own listing/cluster marker rendering ALSO independently
  // filters by a separate `activeStatuses` set (driven by the CNTL status
  // toggles, e.g. defaults to Active-only) - completely independent of
  // `setListings()`'s array. Ask's results can include any status per its
  // own query (e.g. an agent's Active AND Pending listings), so every
  // status must be allowed through here regardless of today's CNTL
  // selection, or non-matching-status results silently vanish from the
  // map even though listings/state.listings has them. Restored on exit.
  map?.setActiveStatuses(['Active', 'Pending', 'Sold', 'OffMarket']);

  applyFiltersToListings();

  if (map) {
    fitMapToPoints(
      askResultListings
        .filter((l) => Number.isFinite(l.lat) && Number.isFinite(l.lng))
        .map((l) => ({ lat: l.lat!, lng: l.lng! }))
    );
  }

  showAskResultBanner();
}

function showAskResultBanner(): void {
  hideAskResultBanner();
  if (!askResultSummary) return;

  const banner = document.createElement('div');
  banner.className = 'ask-result-banner';

  const text = document.createElement('span');
  text.className = 'ask-result-banner-text';
  text.textContent = askResultSummary;

  const closeBtn = document.createElement('button');
  closeBtn.className = 'ask-result-banner-close';
  closeBtn.setAttribute('aria-label', 'Clear Ask result');
  closeBtn.innerHTML = '&times;';
  closeBtn.addEventListener('click', () => {
    filterDrawer?.setAskActive(false);
    clearAskModeState();
    // See handleFiltersToggle's identical comment - mapMarkerListings/listings
    // are stale from before Ask was entered and need a real refetch.
    reloadListings();
  });

  banner.appendChild(text);
  banner.appendChild(closeBtn);
  document.body.appendChild(banner);
  askResultBannerEl = banner;
}

function hideAskResultBanner(): void {
  askResultBannerEl?.remove();
  askResultBannerEl = null;
}

/**
 * Banner variant for ranked-neighborhood Ask results. Deliberately separate
 * from showAskResultBanner()/activateAskMode() - a ranked-neighborhood
 * result doesn't replace the working `listings` set or touch
 * askModeActive/filtersEnabled, so its close button just removes the
 * banner rather than running clearAskModeState()+reloadListings().
 */
function showAskNeighborhoodResultBanner(
  summary: string | null,
  neighborhoodLinks: Array<{ id: string; name: string }>
): void {
  hideAskResultBanner();
  if (!summary && neighborhoodLinks.length === 0) return;

  const banner = document.createElement('div');
  banner.className = 'ask-result-banner';

  const textWrap = document.createElement('span');
  textWrap.className = 'ask-result-banner-text';
  if (summary) {
    textWrap.appendChild(document.createTextNode(summary + ' '));
  }

  neighborhoodLinks.forEach((n, i) => {
    const link = document.createElement('a');
    link.href = '#';
    link.className = 'ask-result-banner-neighborhood-link';
    link.textContent = n.name;
    link.addEventListener('click', (e) => {
      e.preventDefault();
      openNeighborhoodModalFromAskResult(n.id, n.name);
    });
    textWrap.appendChild(link);
    if (i < neighborhoodLinks.length - 1) {
      textWrap.appendChild(document.createTextNode(', '));
    }
  });

  const closeBtn = document.createElement('button');
  closeBtn.className = 'ask-result-banner-close';
  closeBtn.setAttribute('aria-label', 'Clear Ask result');
  closeBtn.innerHTML = '&times;';
  closeBtn.addEventListener('click', () => {
    hideAskResultBanner();
  });

  banner.appendChild(textWrap);
  banner.appendChild(closeBtn);
  document.body.appendChild(banner);
  askResultBannerEl = banner;
}

/** Opens NeighborhoodModal for an Ask neighborhood-result link, matching the
 * {id, name, regionName, primaryImageUrl, totalUnits, forSaleCount} shape
 * every other neighborhoodModal.open() call site in this file uses. */
function openNeighborhoodModalFromAskResult(neighborhoodId: string, neighborhoodName: string): void {
  if (!neighborhoodModal) return;
  const neighborhood = neighborhoods.find((n) => n.id === neighborhoodId);
  neighborhoodModal.open({
    id: neighborhoodId,
    name: neighborhoodName,
    regionName: neighborhood?.regionName,
    primaryImageUrl: neighborhood?.primaryImageUrl,
    totalUnits: neighborhood?.totalUnits,
    forSaleCount: neighborhood?.forSaleCount,
  });
}

/**
 * Initialize property modal
 */
function initPropertyModal(): void {
  propertyModal = new PropertyModal({
    container: document.body,
    context: 'public',
    showContactButton: true,
    contactButtonText: 'Contact Agent',
    showNeighborhoodLink: true,
    showFavoriteButton: true,
    showAlertButton: true,
    onClose: () => {
      history.pushState(null, '', '/');
      // Recenter on whatever was just open, then leave a persistent
      // highlight on its marker - see openPropertyMapTarget. No-ops
      // quietly if we never captured a target (e.g. an open call site that
      // doesn't have coordinates for this listing) or the marker isn't
      // currently rendered as its own dot (clustered/off-screen - same
      // graceful fallback as the desktop hover version).
      if (map && openPropertyMapTarget) {
        map.panTo({ lat: openPropertyMapTarget.lat, lng: openPropertyMapTarget.lng });
        map.highlightListing(openPropertyMapTarget.id);
      }
    },
    onNeighborhoodClick: (neighborhoodId) => {
      // Open neighborhood modal from property modal
      if (neighborhoodModal) {
        const neighborhood = neighborhoods.find(n => n.id === neighborhoodId);
        neighborhoodModal.open({
          id: neighborhoodId,
          name: neighborhood?.name,
          regionName: neighborhood?.regionName,
          primaryImageUrl: neighborhood?.primaryImageUrl,
          totalUnits: neighborhood?.totalUnits,
          forSaleCount: neighborhood?.forSaleCount,
        });
      }
    },
    onLoadMlsData: async (listingId: string): Promise<MLSListing | null> => {
      try {
        return await api.getListing(listingId);
      } catch {
        return null;
      }
    },
    onLoadSalesHistory: async (tmkId: string) => {
      try {
        const sales = await api.getSalesByTmk(tmkId);
        // Convert SaleWithUnit to PropertySale format
        return sales.map(s => ({
          saleDate: s.saleDate,
          salePrice: s.salePrice,
        }));
      } catch {
        return [];
      }
    },
    // Favorites
    isFavorite: (tmk: string) => isFavorite(tmk),
    onFavoriteToggle: async (tmk: string, newState: boolean) => {
      try {
        await toggleFavorite(tmk);
        console.log(`Property ${tmk} favorite: ${newState}`);
      } catch (error) {
        console.error('Failed to toggle favorite:', error);
      }
    },
    // Alerts
    isAlerted: (tmk: string) => hasAlert(tmk),
    isLoggedIn: () => canCreateAlerts(),
    onAlertAuthRequired: (tmk: string) => {
      const listing = listings.find(l => l.tmkNormalized === tmk || l.id === tmk);
      requestAlertSignIn(tmk, listing ? `price changes and sale updates on ${formatListingAddress(listing)}` : 'price changes and sale updates on this home');
    },
    onAlertToggle: async (tmk: string) => {
      try {
        await toggleAlert(tmk);
      } catch (error) {
        console.error('Failed to toggle alert:', error);
      }
    },
    // Contact: open the platform contact modal with the message pre-filled
    // to reference this property. The contact-modal flow itself handles
    // the auth gate (anonymous → sign-in CTA → form), and pendingContactPrefill
    // survives that detour so the message lands in the textarea after sign-in.
    onContactClick: (property) => {
      pendingContactPrefill = buildContactPrefillFor(property);
      document.getElementById('contactBtn')?.click();
    },
  });
}

// Builds a friendly opener message for the contact form given a property.
// Prefers building name + unit, then street address, falls back to MLS#.
function buildContactPrefillFor(p: PropertyModalData): string {
  const buildingPart = p.buildingName
    ? `${p.buildingName}${p.unit ? ` #${p.unit}` : ''}`
    : null;
  const ref = buildingPart || p.address || (p.listingId ? `MLS#${p.listingId}` : 'this property');
  return `Hi Todd — I'm interested in ${ref}. Could you tell me more?`;
}

// =============================================================================
// Open-house iCal generation (the calendar button on listing cards)
// =============================================================================

// Build an .ics file body for a listing's upcoming open house. Returns null
// if the listing has no scheduled open house. Times are converted from HST
// (Maui is UTC-10, no DST) to UTC so the event lands at the right local
// moment regardless of the importing calendar's timezone.
function buildOpenHouseIcs(listing: ListingSummary): string | null {
  const oh = listing.openHouse;
  if (!oh || !oh.date) return null;

  const addressParts = [listing.address, listing.unitNumber ? `#${listing.unitNumber}` : null, listing.city]
    .filter(Boolean).join(' ');
  const summary = `Open house: ${listing.buildingName || listing.address}${listing.unitNumber ? ` #${listing.unitNumber}` : ''}`;
  const description = [
    listing.listPrice ? `Listed at $${Math.round(listing.listPrice).toLocaleString()}` : null,
    listing.bedrooms != null ? `${listing.bedrooms}BR / ${listing.bathrooms ?? '?'}BA` : null,
    listing.livingArea != null ? `${listing.livingArea.toLocaleString()} sqft` : null,
    oh.remarks ? `\\n\\nNotes: ${oh.remarks}` : null,
    listing.listingId ? `\\nMLS# ${listing.listingId}` : null,
  ].filter(Boolean).join(' · ');

  // If we have specific times, build a timed event in UTC. Otherwise fall
  // back to an all-day event using DTSTART;VALUE=DATE.
  let dtStart: string;
  let dtEnd: string;
  if (oh.start && oh.end) {
    const startUtc = hstToUtcStamp(oh.date, oh.start);
    const endUtc = hstToUtcStamp(oh.date, oh.end);
    if (!startUtc || !endUtc) return buildAllDayIcs(oh.date, summary, description, addressParts, listing);
    dtStart = `DTSTART:${startUtc}`;
    dtEnd = `DTEND:${endUtc}`;
  } else {
    return buildAllDayIcs(oh.date, summary, description, addressParts, listing);
  }

  const uid = `oh-${listing.id}-${oh.date}@slowmaui.com`;
  const stamp = utcStampNow();
  const url = `https://slowmaui.com/?p=${encodeURIComponent(listing.listingId || listing.id)}`;

  return [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//Slow Maui//Open House//EN',
    'CALSCALE:GREGORIAN',
    'METHOD:PUBLISH',
    'BEGIN:VEVENT',
    `UID:${uid}`,
    `DTSTAMP:${stamp}`,
    dtStart,
    dtEnd,
    `SUMMARY:${icsEscape(summary)}`,
    `DESCRIPTION:${icsEscape(description)}`,
    `LOCATION:${icsEscape(addressParts)}`,
    `URL:${url}`,
    'END:VEVENT',
    'END:VCALENDAR',
    '',
  ].join('\r\n');
}

function buildAllDayIcs(date: string, summary: string, description: string, address: string, listing: ListingSummary): string {
  // YYYY-MM-DD → YYYYMMDD; DTEND is exclusive so set to next day.
  const [y, m, d] = date.split('-').map(n => parseInt(n, 10));
  const start = `${y}${String(m).padStart(2, '0')}${String(d).padStart(2, '0')}`;
  const next = new Date(Date.UTC(y!, (m! - 1), d! + 1));
  const end = `${next.getUTCFullYear()}${String(next.getUTCMonth() + 1).padStart(2, '0')}${String(next.getUTCDate()).padStart(2, '0')}`;
  const uid = `oh-${listing.id}-${date}@slowmaui.com`;
  return [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//Slow Maui//Open House//EN',
    'CALSCALE:GREGORIAN',
    'METHOD:PUBLISH',
    'BEGIN:VEVENT',
    `UID:${uid}`,
    `DTSTAMP:${utcStampNow()}`,
    `DTSTART;VALUE=DATE:${start}`,
    `DTEND;VALUE=DATE:${end}`,
    `SUMMARY:${icsEscape(summary)}`,
    `DESCRIPTION:${icsEscape(description)}`,
    `LOCATION:${icsEscape(address)}`,
    `URL:https://slowmaui.com/?p=${encodeURIComponent(listing.listingId || listing.id)}`,
    'END:VEVENT',
    'END:VCALENDAR',
    '',
  ].join('\r\n');
}

// Parse "11:00 AM" / "3:00 PM" + a YYYY-MM-DD date as Hawaii local, return
// the UTC timestamp in iCal compact form (YYYYMMDDTHHMMSSZ). Hawaii is
// UTC-10 year-round (no DST). Returns null if the time can't be parsed.
function hstToUtcStamp(dateIso: string, timeStr: string): string | null {
  const m = timeStr.trim().match(/^(\d{1,2}):(\d{2})\s*(AM|PM)?$/i);
  if (!m) return null;
  let hours = parseInt(m[1]!, 10);
  const minutes = parseInt(m[2]!, 10);
  const ampm = m[3]?.toUpperCase();
  if (ampm === 'PM' && hours < 12) hours += 12;
  if (ampm === 'AM' && hours === 12) hours = 0;
  const [y, mo, d] = dateIso.split('-').map(n => parseInt(n, 10));
  // HST → UTC: add 10 hours (Date.UTC + the 10h offset bakes it in).
  const utc = new Date(Date.UTC(y!, (mo! - 1), d!, hours + 10, minutes, 0));
  return formatUtcStamp(utc);
}

function utcStampNow(): string { return formatUtcStamp(new Date()); }
function formatUtcStamp(d: Date): string {
  const Y = d.getUTCFullYear();
  const M = String(d.getUTCMonth() + 1).padStart(2, '0');
  const D = String(d.getUTCDate()).padStart(2, '0');
  const h = String(d.getUTCHours()).padStart(2, '0');
  const m = String(d.getUTCMinutes()).padStart(2, '0');
  const s = String(d.getUTCSeconds()).padStart(2, '0');
  return `${Y}${M}${D}T${h}${m}${s}Z`;
}

// iCal text fields need backslashes, commas, semicolons, and newlines escaped.
function icsEscape(s: string): string {
  return (s || '')
    .replace(/\\/g, '\\\\')
    .replace(/;/g, '\\;')
    .replace(/,/g, '\\,')
    .replace(/\r?\n/g, '\\n');
}

// Trigger a file download in the browser via a synthetic <a download> click.
function downloadFile(name: string, content: string, mime: string): void {
  const blob = new Blob([content], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  a.style.display = 'none';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/**
 * Initialize neighborhood modal
 */
function initNeighborhoodModal(): void {
  neighborhoodModal = new NeighborhoodModal({
    container: document.body,
    showFavoriteButton: true,
    showAlertButton: true,
    onClose: () => {
      history.pushState(null, '', '/');
    },
    onListingClick: (listing) => {
      // Open property modal from neighborhood modal
      handleListingClick(listing);
    },
    onViewOnMap: (neighborhood) => {
      // Enter neighborhood mode on map
      if (map && 'id' in neighborhood) {
        map.enterNeighborhoodMode(neighborhood.id);
        currentNeighborhoodMode = { id: neighborhood.id, name: neighborhood.name };
        applyFiltersToListings();
      }
    },
    onLoadNeighborhoodData: async (neighborhoodId: string): Promise<Neighborhood | null> => {
      try {
        // Use slug-based lookup since API expects slug
        const neighborhood = neighborhoods.find(n => n.id === neighborhoodId);
        if (neighborhood) {
          return await api.getNeighborhood(neighborhood.slug);
        }
        return null;
      } catch {
        return null;
      }
    },
    // Full ListingSummary-shaped, server-scoped by neighborhoodId - NOT
    // filtered from the in-memory `listings` array, which is viewport- and
    // CNTL-status-bounded (see performance work) and would silently miss
    // real listings outside whatever's currently on screen/selected for a
    // neighborhood whose units aren't all within the current view or
    // status filter. Confirmed live: Makalii at Wailea and Makena Surf
    // both have the right listings in the backend but showed 0 and
    // 7-8-instead-of-2 respectively in this tab, purely because of this
    // stale-array filter - the region/district sibling (GeographyModal's
    // onLoadListings below) already uses the correct fetch-fresh pattern.
    onLoadListings: async (neighborhoodId: string): Promise<ListingSummary[]> => {
      try {
        return await api.getMapMarkers({ neighborhoodId, status: 'Active,Pending' });
      } catch (error) {
        console.error('Failed to load neighborhood listings:', error);
        return [];
      }
    },
    onLoadSales: async (neighborhoodId: string): Promise<NeighborhoodSalesChartData | null> => {
      try {
        // Find neighborhood to get slug for API call
        const neighborhood = neighborhoods.find(n => n.id === neighborhoodId);
        if (!neighborhood) return null;

        const salesData = await api.getNeighborhoodSales(neighborhood.slug, { chart: true });
        return {
          salesCount1yr: salesData.salesCount1yr,
          avgSalePrice: salesData.avgSalePrice,
          avgDaysOnMarket: salesData.avgDaysOnMarket,
          pricePerSqft: salesData.pricePerSqft,
          chartData: salesData.chartData,
        };
      } catch (error) {
        console.error('Failed to load sales data:', error);
        return null;
      }
    },
    onLoadActivity: async (neighborhoodId: string, days: number) => {
      try {
        const neighborhood = neighborhoods.find(n => n.id === neighborhoodId);
        if (!neighborhood) return null;
        return await api.getNeighborhoodActivity(neighborhood.slug, { days });
      } catch (error) {
        console.error('Failed to load neighborhood activity:', error);
        return null;
      }
    },
    onActivityListingClick: (listingId: string) => {
      neighborhoodModal?.close();
      void openListingFromQuery(listingId);
    },
    // Favorites (for neighborhoods, use neighborhood ID as key)
    isFavorite: (neighborhoodId: string) => isFavorite(`nh:${neighborhoodId}`),
    onFavoriteToggle: async (neighborhoodId: string, newState: boolean) => {
      try {
        await toggleFavorite(`nh:${neighborhoodId}`);
        console.log(`Neighborhood ${neighborhoodId} favorite: ${newState}`);
      } catch (error) {
        console.error('Failed to toggle favorite:', error);
      }
    },
    // Alerts
    isAlerted: (neighborhoodId: string) => hasAlert(`nh:${neighborhoodId}`),
    isLoggedIn: () => canCreateAlerts(),
    onAlertAuthRequired: (neighborhoodId: string) => {
      const nh = neighborhoods.find(n => n.id === neighborhoodId);
      requestAlertSignIn(`nh:${neighborhoodId}`, nh ? `new listings in ${formatNeighborhoodName(nh.name)}` : 'new listings here');
    },
    onAlertToggle: async (neighborhoodId: string) => {
      try {
        await toggleAlert(`nh:${neighborhoodId}`);
      } catch (error) {
        console.error('Failed to toggle alert:', error);
      }
    },
  });
}

/**
 * Initialize the Region/District info popup. One shared instance for both -
 * the component only branches on entityType for display (eyebrow/title
 * styling), so the data-loading callbacks below just switch API calls based
 * on `data.entityType` from whatever GeographyModalData the click handler
 * passed to open().
 */
function initGeographyModal(): void {
  geographyModal = new GeographyModal({
    container: document.body,
    onListingClick: (listing) => {
      handleListingClick(listing);
    },
    onLoadEntityData: async (data: GeographyModalData) => {
      try {
        if (data.entityType === 'county') {
          const county = await api.getCounty(data.id);
          return {
            id: county.id,
            name: county.name,
            slug: county.id,
            description: county.description,
            media: county.media,
            polygon: county.polygon,
          };
        }
        if (data.entityType === 'region') {
          const region = await api.getRegion(data.slug);
          return {
            id: region.id,
            name: region.name,
            slug: region.slug,
            description: region.description,
            media: region.media,
            polygon: region.polygon,
            content: region.content,
          };
        }
        const district = await api.getDistrict(data.slug);
        return {
          id: district.id,
          name: district.name,
          slug: district.slug,
          description: district.description,
          regionName: district.regionName,
          media: district.media,
          polygon: district.polygon,
          content: district.content,
        };
      } catch (error) {
        console.error('Failed to load geography entity data:', error);
        return null;
      }
    },
    // Full ListingSummary-shaped, server-scoped by district_id/region_id -
    // NOT filtered from the in-memory `listings` array, which is now
    // viewport-bounded (see performance work) and would silently miss
    // listings outside whatever's currently on screen for a region/district
    // that spans a wider area than the current view.
    onLoadListings: async (data: GeographyModalData): Promise<ListingSummary[]> => {
      try {
        // County has no scope param of its own - there's only one county
        // (it spans every region), so omitting regionId/districtId entirely
        // already returns every Active/Pending listing islandwide, which is
        // exactly what "county-wide" means here.
        return await api.getMapMarkers({
          status: 'Active,Pending',
          districtId: data.entityType === 'district' ? data.id : undefined,
          regionId: data.entityType === 'region' ? data.id : undefined,
        });
      } catch (error) {
        console.error('Failed to load geography listings:', error);
        return [];
      }
    },
    onLoadSales: async (data: GeographyModalData): Promise<NeighborhoodSalesChartData | null> => {
      try {
        const salesData = data.entityType === 'county'
          ? await api.getCountySales(data.id, { chart: true })
          : data.entityType === 'region'
          ? await api.getRegionSales(data.slug, { chart: true })
          : await api.getDistrictSales(data.slug, { chart: true });
        return {
          salesCount1yr: salesData.salesCount1yr,
          avgSalePrice: salesData.avgSalePrice,
          avgDaysOnMarket: salesData.avgDaysOnMarket,
          pricePerSqft: salesData.pricePerSqft,
          chartData: salesData.chartData,
        };
      } catch (error) {
        console.error('Failed to load geography sales data:', error);
        return null;
      }
    },
    onLoadMarketData: async (data: GeographyModalData): Promise<MarketTrendsChartData | null> => {
      try {
        // 36 months = MLS's own 3-year retention ceiling on Sold/off-market
        // listings - requesting more just returns nothing extra. Real
        // history is shorter than that today (mls_listings' close_date only
        // goes back to ~May 2025, since real-time Sold capture is new this
        // session), so this shows what's actually there now and grows
        // automatically as more real history accrues, no future code change
        // needed.
        // Returns all three Condo/House/All segments already - the
        // MarketTrendsChart component's own selector switches between them
        // client-side, no further shaping needed here.
        return data.entityType === 'county'
          ? await api.getCountyMarketTrends(data.id, { months: 36 })
          : data.entityType === 'region'
          ? await api.getRegionMarketTrends(data.slug, { months: 36 })
          : await api.getDistrictMarketTrends(data.slug, { months: 36 });
      } catch (error) {
        console.error('Failed to load geography market trends data:', error);
        return null;
      }
    },
    // Current-snapshot headline strip shown above the trend chart (see
    // packages/ui-components' GeographyModal.buildMarketHeadline) - same
    // /stats/overview MarketStats' Overview tab uses, scoped to just this
    // one region/district's district id(s) instead of a multi-district
    // picker. A region's scope is every district that belongs to it.
    onLoadMarketHeadline: async (data: GeographyModalData) => {
      try {
        // County scope = every district, since there's currently only one
        // county (it spans every region).
        const scopedDistricts = data.entityType === 'county'
          ? districts.map(d => d.id)
          : data.entityType === 'region'
          ? districts.filter(d => d.regionId === data.id).map(d => d.id)
          : [data.id];
        const response = await api.getStatsOverview({ districts: scopedDistricts, window: '90d' });
        return response.headline;
      } catch (error) {
        console.error('Failed to load geography market headline:', error);
        return null;
      }
    },
    onLoadActivity: async (data: GeographyModalData, days: number) => {
      try {
        return data.entityType === 'county'
          ? await api.getCountyActivity(data.id, { days })
          : data.entityType === 'region'
          ? await api.getRegionActivity(data.slug, { days })
          : await api.getDistrictActivity(data.slug, { days });
      } catch (error) {
        console.error('Failed to load geography activity data:', error);
        return null;
      }
    },
    onActivityListingClick: (listingId: string) => {
      geographyModal?.close();
      void openListingFromQuery(listingId);
    },
    // Alerts - "subscribe to updates in this district/region", same
    // login-required gate as the neighborhood/property bells (see
    // utils/alerts.ts and requestAlertSignIn).
    showAlertButton: true,
    isAlerted: (id, entityType) => hasAlert(`${geographyAlertKeyPrefix(entityType)}:${id}`),
    isLoggedIn: () => canCreateAlerts(),
    onAlertAuthRequired: (id, entityType) => {
      const key = `${geographyAlertKeyPrefix(entityType)}:${id}`;
      const entity = entityType === 'county' ? counties.find(co => co.id === id)
        : entityType === 'region' ? regions.find(r => r.id === id)
        : districts.find(d => d.id === id);
      requestAlertSignIn(key, entity ? `new listings and price changes in ${entity.name}` : `new listings and price changes in this ${entityType}`);
    },
    onAlertToggle: async (id, entityType) => {
      try {
        await toggleAlert(`${geographyAlertKeyPrefix(entityType)}:${id}`);
      } catch (error) {
        console.error('Failed to toggle geography alert:', error);
      }
    },
  });
}

function geographyAlertKeyPrefix(entityType: GeographyEntityType): 'c' | 'r' | 'd' {
  if (entityType === 'county') return 'c';
  if (entityType === 'region') return 'r';
  return 'd';
}

/**
 * Handle a County label click on the map - same as handleRegionMapClick.
 */
function handleCountyMapClick(county: GeographySummary): void {
  geographyModal?.open({
    id: county.id,
    slug: county.slug,
    entityType: 'county',
    name: county.name,
    neighborhoodCount: county.neighborhoodCount,
    forSaleCount: county.forSaleCount,
  });
}

/**
 * Handle a Region label click on the map - opens the GeographyModal in
 * place (no map recentering, per the confirmed UX decision).
 */
function handleRegionMapClick(region: GeographySummary): void {
  geographyModal?.open({
    id: region.id,
    slug: region.slug,
    entityType: 'region',
    name: region.name,
    neighborhoodCount: region.neighborhoodCount,
    forSaleCount: region.forSaleCount,
  });
}

/**
 * Handle a District label click on the map - same as handleRegionMapClick.
 */
function handleDistrictMapClick(district: GeographySummary): void {
  geographyModal?.open({
    id: district.id,
    slug: district.slug,
    entityType: 'district',
    name: district.name,
    regionName: district.regionName,
    neighborhoodCount: district.neighborhoodCount,
    forSaleCount: district.forSaleCount,
  });
}

/**
 * Initialize header (persistent nav actions: Contact/Account; the intro/
 * help modal; the two in-DOM modals those actions open).
 */
function initHeader(): void {
  const contactBtn = document.getElementById('contactBtn');
  const contactModal = document.getElementById('contact-modal');
  const contactModalClose = document.getElementById('contactModalClose');
  const contactModalOverlay = contactModal?.querySelector('.modal-overlay');
  const contactForm = document.getElementById('contactForm') as HTMLFormElement | null;

  // Auth-gated contact: signed-in users see a pre-filled compose form;
  // anonymous users see a sign-in CTA. Re-renders the modal body each time it
  // opens (and on auth state change) so toggling sign-in mid-session shows the
  // right state without a refresh.
  const contactAuthGate = document.getElementById('contactAuthGate');
  const contactSignedInAs = document.getElementById('contactSignedInAs');
  const contactSignInBtn = document.getElementById('contactSignInBtn');

  const refreshContactModalState = () => {
    if (!contactForm || !contactAuthGate) return;
    if (currentUser) {
      contactAuthGate.classList.add('hidden');
      contactForm.classList.remove('hidden');
      if (contactSignedInAs) {
        const display = currentUser.displayName || currentUser.email || 'your account';
        const emailLine = currentUser.email && currentUser.email !== display
          ? ` &lt;${escapeHtmlText(currentUser.email)}&gt;`
          : '';
        contactSignedInAs.innerHTML = `Sending as <strong>${escapeHtmlText(display)}</strong>${emailLine}`;
      }
    } else {
      contactAuthGate.classList.remove('hidden');
      contactForm.classList.add('hidden');
    }
    // Clear any stale status from a previous open.
    const statusEl = document.getElementById('contactFormStatus');
    if (statusEl) {
      statusEl.textContent = '';
      statusEl.className = 'contact-form-status';
    }
    // Consume any pending pre-fill from a "Contact Agent" click on a
    // PropertyModal. Only seeds the textarea if it's empty (don't trample
    // text the user may have already typed) and only when the form is
    // showing — for the auth-gated case, the prefill survives until the
    // user signs in and refreshContactModalState fires again.
    if (currentUser && pendingContactPrefill) {
      const messageInput = document.getElementById('contactMessage') as HTMLTextAreaElement | null;
      if (messageInput && !messageInput.value.trim()) {
        messageInput.value = pendingContactPrefill;
      }
      pendingContactPrefill = null;
    }
  };

  // Contact button opens modal
  const openContactModal = () => {
    refreshContactModalState();
    contactModal?.classList.remove('hidden');
  };

  contactBtn?.addEventListener('click', openContactModal);

  // Mobile hamburger (per Todd 2026-10-24) - toggles the tabs drawer;
  // see main.css's .persistent-nav-tabs.open for the CSS half of this.
  // Matches the same toggle added to published agent pages' nav
  // (packages/agent-nav-chrome's NAV_HAMBURGER_TOGGLE_SCRIPT).
  const navHamburger = document.getElementById('persistent-nav-hamburger');
  const navTabs = document.getElementById('persistent-nav-tabs');
  navHamburger?.addEventListener('click', () => {
    const isOpen = navTabs?.classList.toggle('open');
    navHamburger.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
  });

  // Close contact modal
  const closeContactModal = () => {
    contactModal?.classList.add('hidden');
  };

  contactModalClose?.addEventListener('click', closeContactModal);
  contactModalOverlay?.addEventListener('click', closeContactModal);

  // Sign-in CTA in the contact modal: closes contact modal, opens auth modal,
  // and remembers the user wanted to contact so we can re-open after sign-in.
  contactSignInBtn?.addEventListener('click', () => {
    pendingContactReopen = true;
    closeContactModal();
    resetAuthIntroCopy();
    document.getElementById('auth-modal')?.classList.remove('hidden');
  });

  // Re-render modal whenever auth state changes (covers: user signs in via the
  // gate's Sign In button → onAuthStateChange fires → we flip from gate to
  // form). If the contact modal was suppressed pending sign-in, reopen it.
  onAuthStateChange(() => {
    if (contactModal && !contactModal.classList.contains('hidden')) {
      refreshContactModalState();
    }
    if (currentUser && pendingContactReopen) {
      pendingContactReopen = false;
      openContactModal();
    }
  });

  // Contact form submission — name/email come from the authenticated user, not
  // from input fields (they don't exist anymore). Submit guards against the
  // gate-bypass case (user signed out between rendering the form and clicking
  // Send) by re-rendering the gate.
  contactForm?.addEventListener('submit', async (e) => {
    e.preventDefault();

    if (!currentUser) {
      refreshContactModalState();
      return;
    }

    const messageInput = document.getElementById('contactMessage') as HTMLTextAreaElement;
    const statusEl = document.getElementById('contactFormStatus');
    const submitBtn = contactForm.querySelector('button[type="submit"]') as HTMLButtonElement;

    const message = messageInput?.value.trim();
    if (!message) {
      if (statusEl) {
        statusEl.textContent = 'Please write a message.';
        statusEl.className = 'contact-form-status error';
      }
      return;
    }

    submitBtn.disabled = true;
    submitBtn.textContent = 'Sending...';
    if (statusEl) {
      statusEl.textContent = '';
      statusEl.className = 'contact-form-status';
    }

    try {
      const response = await fetch('https://slowmaui-contact-api.slowmaui-mls-api.workers.dev/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: currentUser.displayName || currentUser.email,
          email: currentUser.email,
          message,
          source: 'contact-v2',
          // Single-tenant fallback today; multi-agent payload upgrade is on
          // the roadmap (priority #2). Once site-wide context is threaded
          // through, also pass agentSubdomain / sourceUrl / neighborhoodId.
        }),
      });

      const result = await response.json();

      if (result.success) {
        if (statusEl) {
          statusEl.textContent = "Message sent! I'll get back to you soon.";
          statusEl.className = 'contact-form-status success';
        }
        contactForm.reset();
        setTimeout(closeContactModal, 2500);
      } else {
        throw new Error(result.error || 'Failed to send');
      }
    } catch (error) {
      console.error('Contact error:', error);
      if (statusEl) {
        statusEl.textContent = 'Sorry, there was an error. Please try again.';
        statusEl.className = 'contact-form-status error';
      }
    } finally {
      submitBtn.disabled = false;
      submitBtn.textContent = 'Send Message';
    }
  });

  // User account button - opens auth modal
  const userAccountBtn = document.getElementById('userAccountBtn');
  const authModal = document.getElementById('auth-modal');

  const handleAccountClick = () => {
    resetAuthIntroCopy();
    authModal?.classList.remove('hidden');
  };

  userAccountBtn?.addEventListener('click', handleAccountClick);

  // Intro modal (Welcome/Help) - trigger is the "?" button on the map's
  // Search/Ask sub-header (previously a hamburger-menu entry).
  const introModal = document.getElementById('intro-modal');
  const introModalClose = document.getElementById('introModalClose');
  const introModalOverlay = introModal?.querySelector('.modal-overlay');
  const introStartBtn = document.getElementById('introStartBtn');
  const introNoShowAgain = document.getElementById('introNoShowAgain') as HTMLInputElement | null;
  const helpBtn = document.getElementById('helpBtn');

  const closeIntroModal = () => {
    introModal?.classList.add('hidden');
    // If "Don't show again" is checked, save to localStorage
    if (introNoShowAgain?.checked) {
      localStorage.setItem('slowmaui-visited', 'true');
    }
  };

  introModalClose?.addEventListener('click', closeIntroModal);
  introModalOverlay?.addEventListener('click', closeIntroModal);
  introStartBtn?.addEventListener('click', closeIntroModal);
  helpBtn?.addEventListener('click', () => {
    introModal?.classList.remove('hidden');
  });

  // Close modals on Escape key
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && !introModal?.classList.contains('hidden')) {
      closeIntroModal();
    }
  });
}

/**
 * Show intro modal for first-time visitors
 * Only shows if not logged in and hasn't visited before
 */
function showIntroModalIfFirstVisit(isLoggedIn: boolean): void {
  const hasVisited = localStorage.getItem('slowmaui-visited');
  if (!hasVisited && !isLoggedIn) {
    const introModal = document.getElementById('intro-modal');
    introModal?.classList.remove('hidden');
  }
}

// Ask AI overlay. The old Market Stats modal (removed - its hamburger-menu
// entry was cut; GeographyModal's own Market Data tab already shows the
// same /stats/overview data, scoped to whichever region/district the user
// clicked, which covers most of what it was for) mounted into a static
// shell already in index.html; AskOverlay builds its own full-screen backdrop
// internally - same self-contained convention as GeographyModal
// (container: document.body) - so no shell markup is needed here. A fresh
// instance per open naturally gives v1's "single-shot, no retained state
// across opens" behavior for free.
let askOverlay: AskOverlay | null = null;

function openAskOverlay(initialQuery?: string): void {
  askOverlay?.destroy();
  askOverlay = new AskOverlay({
    container: document.body,
    onAsk: (query) => api.askSearch(query),
    onShowOnMap: (response) => {
      if (response.resultType === 'neighborhoods') {
        handleAskShowOnMapNeighborhoods(response);
      } else {
        void handleAskShowOnMap(response);
      }
    },
    onClose: () => {
      askOverlay?.destroy();
      askOverlay = null;
    },
  });
  askOverlay.open(initialQuery);
}

// Hydrates Ask's reduced AskSearchListing results into full ListingSummary
// objects (lat/lng, listingId, etc.) via the bulk by-ids endpoint, then
// activates Ask mode so they're plotted on the real map + sidebar - the
// overlay itself already closed (self-closes on "Show on Map" click).
async function handleAskShowOnMap(response: AskSearchResponse): Promise<void> {
  const ids = response.results.map((r) => r.listing.id);
  if (ids.length === 0) return;

  try {
    const hydrated = await api.getListingsByIds(ids);
    activateAskMode(hydrated, response.summary);
  } catch (error) {
    console.error('Failed to load Ask results for map:', error);
  }
}

// Lighter-weight counterpart to handleAskShowOnMap() for ranked-neighborhood
// Ask results - doesn't touch the working `listings` set, filters, or
// neighborhood mode the way activateAskMode() does, since a handful of
// highlighted neighborhoods is compatible with whatever's already on
// screen rather than needing to replace it.
function handleAskShowOnMapNeighborhoods(response: AskSearchResponse): void {
  const results: AskSearchNeighborhood[] = response.neighborhoodResults ?? [];
  if (results.length === 0) return;

  fitMapToPoints(results.map((n) => ({ lat: n.centerLat, lng: n.centerLng })));

  showAskNeighborhoodResultBanner(
    response.summary,
    results.map((n) => ({ id: n.id, name: n.name }))
  );
}

/**
 * Get the current subdomain for agent association
 */
function getSubdomain(): string {
  const hostname = window.location.hostname;
  // Bare apex (slowmaui.com) and www both have NO real subdomain - parts[0]
  // for the apex case is "slowmaui" itself (the registrable domain's own
  // name), not an agent slug. The old parts.length >= 2 check below treated
  // slowmaui.com exactly like <agent>.localhost (also 2 parts) and
  // silently returned "slowmaui" as if it were a subdomain - harmless
  // everywhere it was only used for invisible things (contact routing,
  // user creation's subdomain tag), but visibly broke loadSiteAgentBranding()
  // since /agents/slowmaui 404s and that fails open to generic text.
  if (hostname === 'slowmaui.com' || hostname === 'www.slowmaui.com' || hostname === 'localhost') {
    return 'todd';
  }
  const parts = hostname.split('.');
  // subdomain.slowmaui.com or subdomain.localhost
  if (parts.length >= 2) {
    return parts[0];
  }
  return 'todd';
}

/**
 * Resolves whichever agent owns the current subdomain (getSubdomain() -
 * 'todd' by default, same fallback as user creation/contact routing) and
 * personalizes every "Contact <X>" surface with their first name: the
 * persistent header's Contact button, the Contact modal's profile card,
 * and PropertyModal's footer button. Also applies brand colors (see
 * applyAgentBrandColors below) from the same lookup. Fails open to each
 * surface's existing generic/hardcoded text on any error (lookup miss,
 * network failure) rather than showing nothing.
 */
async function loadSiteAgentBranding(): Promise<void> {
  let fullName: string | null = null;
  try {
    const profile = await api.getAgentProfile(getSubdomain());
    fullName = profile.name;
    applyAgentBrandColors(profile.brandPrimaryColor, profile.brandAccentColor);

    // Phone/photo (per Todd 2026-10-25) - a caller may prefer to call
    // instead of emailing, so the Contact modal's phone is a real tel:
    // link, not just text. Each fails open to the existing hardcoded
    // markup independently (e.g. an agent with a photo but no phone on
    // file still gets their photo).
    if (profile.photoUrl) {
      const photo = document.getElementById('contactModalAgentPhoto') as HTMLImageElement | null;
      if (photo) photo.src = profile.photoUrl;
    }
    if (profile.phone) {
      const phoneEl = document.getElementById('contactModalAgentPhone') as HTMLAnchorElement | null;
      if (phoneEl) {
        phoneEl.textContent = profile.phone;
        phoneEl.href = `tel:${toTelHref(profile.phone)}`;
      }
    }
  } catch (error) {
    console.error('Failed to load site agent profile:', error);
  }
  if (!fullName) return;

  const firstName = fullName.trim().split(/\s+/)[0] || null;
  if (!firstName) return;

  const contactLabel = `Contact ${firstName}`;
  const contactBtn = document.getElementById('contactBtn');
  if (contactBtn) contactBtn.textContent = contactLabel;

  const contactModalAgentName = document.getElementById('contactModalAgentName');
  if (contactModalAgentName) contactModalAgentName.textContent = fullName;

  propertyModal?.setContactButtonText(contactLabel);
}

// Strips a stored phone number down to a tel: href - assumes US/Canada
// (10 digits) when no country code is present, since this is a Hawaii
// real estate site; falls back to the raw digits otherwise rather than
// guessing a country code.
function toTelHref(phone: string): string {
  const digits = phone.replace(/\D/g, '');
  if (digits.length === 10) return `+1${digits}`;
  if (digits.length === 11 && digits.startsWith('1')) return `+${digits}`;
  return digits;
}

// Same Material Design CSS custom properties pageChrome.js's buildBaseStyles
// sets for every published agent custom page (workers/agent-api/src/
// pageChrome.js) - applied here so the persistent header (Phase 4) doesn't
// visually jolt when a visitor moves between a custom page and Properties.
// Falls back to the same defaults pageChrome.js uses when an agent hasn't
// set brand colors.
function applyAgentBrandColors(primaryColor: string | null, accentColor: string | null): void {
  const primary = primaryColor || '#2c5530';
  const accent = accentColor || '#1e3a20';
  const root = document.documentElement.style;
  root.setProperty('--primary', primary);
  root.setProperty('--accent', accent);
  root.setProperty('--primary-text', contrastTextColor(primary));
}

// Duplicated from workers/agent-api/src/pageChrome.js's relativeLuminance/
// contrastTextColor (also duplicated into workers/agent-site/src/index.js) -
// apps/public is a separate app with no shared import across these, per
// this codebase's established small-logic-duplication convention. Keep in
// sync by hand if either changes.
function relativeLuminance(r: number, g: number, b: number): number {
  const lin = (c: number) => (c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4));
  return 0.2126 * lin(r) + 0.7152 * lin(g) + 0.0722 * lin(b);
}

function contrastTextColor(hex: string): string {
  const m = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex || '');
  if (!m) return '#ffffff';
  const [r, g, b] = [m[1]!, m[2]!, m[3]!].map((h) => parseInt(h, 16) / 255);
  const bgLum = relativeLuminance(r!, g!, b!);
  const darkTextLum = relativeLuminance(0x1f / 255, 0x29 / 255, 0x37 / 255);
  const contrastWith = (lum: number) => {
    const lighter = Math.max(bgLum, lum);
    const darker = Math.min(bgLum, lum);
    return (lighter + 0.05) / (darker + 0.05);
  };
  return contrastWith(darkTextLum) > contrastWith(1.0) ? '#1f2937' : '#ffffff';
}

/**
 * Sync user data with the API on login
 * - Gets ID token and sets it on API client
 * - Checks if user exists (GET /users/me)
 * - Creates user if new, syncing localStorage favorites
 * - Loads favorites and alerts from API
 */
async function syncUserWithApi(user: AuthUser): Promise<void> {
  try {
    // Get Firebase ID token
    const idToken = await getIdToken();
    if (!idToken) {
      console.error('Failed to get ID token');
      return;
    }

    // Set token on API client
    api.setAuthToken(idToken);

    // Check if user exists in DB
    const existingUser = await api.getCurrentUser();

    if (existingUser) {
      // User exists - load their favorites and alerts from API
      console.log('User exists, loading data from API');
      const [favoritesData, alertsData] = await Promise.all([
        api.getFavorites(),
        api.getAlerts(),
      ]);

      initFavoritesForUser(api, favoritesData);
      initAlertsForUser(api, alertsData);

      // Clear any localStorage data since we're using the DB now
      clearLocalStorageFavorites();
    } else {
      // New user - create account and sync localStorage favorites. Alerts
      // require login now (see utils/alerts.ts), so there's never anything
      // anonymous to sync for those - always starts empty.
      console.log('New user, creating account');
      const subdomain = getSubdomain();
      const localFavorites = getLocalStorageFavorites();

      const result = await api.createUser({
        subdomain,
        favorites: localFavorites,
        alerts: [],
      });

      console.log(`User created: ${result.id}, favorites imported: ${result.favoritesImported}`);

      // Clear localStorage since favorites are now in DB
      clearLocalStorageFavorites();

      // Initialize favorites and alerts for the new user
      initFavoritesForUser(api, localFavorites);
      initAlertsForUser(api, []);
    }

    // Alerts are now fully usable (isLoggedIn/dataClient set inside
    // initAlertsForUser above) - apply whatever alert the user attempted
    // before being gated to sign in, if any. Must happen after both
    // branches above, not from a separate onAuthStateChange listener - see
    // applyPendingAlertIfAny's doc comment for why.
    await applyPendingAlertIfAny();
  } catch (error) {
    console.error('Failed to sync user with API:', error);
    // Fall back to anonymous mode on error
    api.setAuthToken(null);
    resetFavoritesToAnonymous();
    resetAlertsToAnonymous();
  }
}

/**
 * Initialize authentication
 */
function initAuth(): void {
  const authModal = document.getElementById('auth-modal');
  const authModalClose = document.getElementById('authModalClose');
  const authModalOverlay = authModal?.querySelector('.modal-overlay');
  const authModalTitle = document.getElementById('authModalTitle');
  const signInView = document.getElementById('authSignInView');
  const profileView = document.getElementById('authProfileView');
  const googleSignInBtn = document.getElementById('googleSignInBtn');
  const appleSignInBtn = document.getElementById('appleSignInBtn');
  const signOutBtn = document.getElementById('signOutBtn');
  const userPhotoEl = document.getElementById('authUserPhoto') as HTMLImageElement;
  const userNameEl = document.getElementById('authUserName');
  const userEmailEl = document.getElementById('authUserEmail');
  const accountInitial = document.getElementById('accountInitial');
  const accountIcon = document.querySelector('.account-icon');
  const menuAccountText = document.getElementById('menuAccountText');

  // Close auth modal
  const closeAuthModal = () => {
    authModal?.classList.add('hidden');
  };

  // Explicit cancel (X / overlay click) - unlike closeAuthModal() itself,
  // which is also called right after a successful sign-in (where a pending
  // alert must survive to be applied), these two really do mean "I don't
  // want to do this" - clear the pending alert per "you must sign in" and
  // restore the generic intro copy.
  const cancelAuthModal = () => {
    pendingAlertKey = null;
    resetAuthIntroCopy();
    closeAuthModal();
  };

  authModalClose?.addEventListener('click', cancelAuthModal);
  authModalOverlay?.addEventListener('click', cancelAuthModal);

  // Manage account → close modal and route to /account.
  const manageAccountBtn = document.getElementById('manageAccountBtn');
  manageAccountBtn?.addEventListener('click', (e) => {
    e.preventDefault();
    closeAuthModal();
    history.pushState(null, '', '/account');
    AccountPage.show();
  });

  // Track if this is the first auth state check (for showing intro modal)
  let isFirstAuthCheck = true;

  // Update UI based on auth state
  const updateAuthUI = async (user: AuthUser | null) => {
    currentUser = user;

    if (user) {
      // User logged in - sync with API
      await syncUserWithApi(user);
    } else {
      // User logged out - reset to anonymous
      api.setAuthToken(null);
      resetFavoritesToAnonymous();
      resetAlertsToAnonymous();
    }

    // Show intro modal on first visit (only on initial auth check)
    if (isFirstAuthCheck) {
      isFirstAuthCheck = false;
      showIntroModalIfFirstVisit(!!user);
    }

    if (user) {
      // User is logged in - show profile view
      signInView?.classList.add('hidden');
      profileView?.classList.remove('hidden');
      authModalTitle!.textContent = 'Account';

      // Update profile info
      if (userPhotoEl && user.photoURL) {
        userPhotoEl.src = user.photoURL;
        userPhotoEl.style.display = 'block';
      } else if (userPhotoEl) {
        userPhotoEl.style.display = 'none';
      }
      if (userNameEl) userNameEl.textContent = user.displayName || 'User';
      if (userEmailEl) userEmailEl.textContent = user.email || '';

      // Update header - show initial instead of icon
      if (accountInitial && accountIcon) {
        const initial = (user.displayName || user.email || 'U')[0].toUpperCase();
        accountInitial.textContent = initial;
        accountInitial.style.display = 'flex';
        (accountIcon as HTMLElement).style.display = 'none';
      }

      // Update mobile menu text
      if (menuAccountText) menuAccountText.textContent = 'Account';
    } else {
      // User is logged out - show sign in view
      signInView?.classList.remove('hidden');
      profileView?.classList.add('hidden');
      authModalTitle!.textContent = 'Sign In';

      // Update header - show icon
      if (accountInitial && accountIcon) {
        accountInitial.style.display = 'none';
        (accountIcon as HTMLElement).style.display = 'block';
      }

      // Update mobile menu text
      if (menuAccountText) menuAccountText.textContent = 'Sign In';
    }
  };

  // Subscribe to auth state changes
  onAuthStateChange(updateAuthUI);

  // onAuthStateChange only fires on sign-in/out, never on Firebase's own
  // silent token refresh (~hourly) - api's cached token would otherwise go
  // stale after about an hour with the tab left open, and every
  // authenticated request (Manage Account, favorites, alerts) would start
  // failing with a 401 despite the user still being genuinely signed in.
  // onIdTokenChange fires on refresh too; just re-cache the token here,
  // don't run the full updateAuthUI/syncUserWithApi flow on every tick.
  onIdTokenChange(async (user) => {
    if (!user) return;
    const idToken = await getIdToken();
    if (idToken) api.setAuthToken(idToken);
  });

  // Google sign in
  googleSignInBtn?.addEventListener('click', async () => {
    try {
      googleSignInBtn.setAttribute('disabled', 'true');
      await signInWithGoogle();
      closeAuthModal();
    } catch (error) {
      console.error('Google sign in error:', error);
      alert('Sign in failed. Please try again.');
    } finally {
      googleSignInBtn.removeAttribute('disabled');
    }
  });

  // Apple sign in
  appleSignInBtn?.addEventListener('click', async () => {
    try {
      appleSignInBtn.setAttribute('disabled', 'true');
      await signInWithApple();
      closeAuthModal();
    } catch (error) {
      console.error('Apple sign in error:', error);
      alert('Sign in failed. Please try again.');
    } finally {
      appleSignInBtn.removeAttribute('disabled');
    }
  });

  // Sign out
  signOutBtn?.addEventListener('click', async () => {
    try {
      await signOut();
      closeAuthModal();
    } catch (error) {
      console.error('Sign out error:', error);
    }
  });
}

/**
 * Load neighborhoods data (fetch only, doesn't update map)
 * Used during initial parallel load when map may not be ready.
 *
 * Lightweight/summary mode - no polygons (see api.getNeighborhoods' doc and
 * the backend route for the measured payload breakdown). Enough for the
 * map's bubble markers (name/lat/lng/forSaleCount), which never read
 * polygon data anyway. loadFullNeighborhoodPolygons() fetches the real,
 * polygon-bearing data right after and swaps it in - see that function and
 * ensureFullNeighborhoodsLoaded() for how Neighborhood Mode entry (which
 * DOES need a real polygon) avoids racing ahead of it.
 */
async function loadNeighborhoodsData(): Promise<void> {
  try {
    const response = await api.getNeighborhoods({
      filters: { visible: true },
      limit: 500, // Load all visible neighborhoods
      summary: true,
    });
    neighborhoods = response.data;
    console.log(`Loaded ${neighborhoods.length} neighborhood summaries (bubbles only, polygons pending)`);
  } catch (error) {
    console.error('Failed to load neighborhoods:', error);
  }
}

// Set once the background full-polygon load kicks off (right after the
// lightweight load above paints bubbles) - see loadFullNeighborhoodPolygons.
let fullNeighborhoodsLoaded: Promise<void> | null = null;

/**
 * Background upgrade: fetches the full (polygon-bearing) neighborhoods
 * response and swaps it into both the local `neighborhoods` array and the
 * map, without blocking first paint. Calling map.setNeighborhoods() again
 * is safe/cheap here - the lightweight pass's empty polygons already made
 * addNeighborhood() skip polygon creation entirely for every neighborhood
 * (it no-ops without polygon data), so this is the first time real
 * google.maps.Polygon objects actually get created, and it happens in the
 * background instead of blocking bubbles from appearing.
 */
async function loadFullNeighborhoodPolygons(): Promise<void> {
  try {
    const response = await api.getNeighborhoods({
      filters: { visible: true },
      limit: 500,
    });
    neighborhoods = response.data;
    if (map) {
      map.setNeighborhoods(neighborhoods);
    }
    console.log(`Upgraded to ${neighborhoods.length} full neighborhoods (polygons attached)`);
  } catch (error) {
    console.error('Failed to load full neighborhood polygons:', error);
  }
}

/**
 * Neighborhood Mode entry needs a real polygon (bounds/zoom are computed
 * from it) - Map.ts's enterNeighborhoodMode bails out with a warning if the
 * neighborhood it looks up has an empty one. Every deep-link/router path
 * that can enter Neighborhood Mode during initial load (before the
 * background full-polygon load has necessarily finished) awaits this
 * first; user-triggered entry (clicking a bubble/listing/search result
 * after the page is already interactive) doesn't need to, since the
 * background load will always have won that race by then.
 */
async function ensureFullNeighborhoodsLoaded(): Promise<void> {
  if (fullNeighborhoodsLoaded) await fullNeighborhoodsLoaded;
}

/**
 * Load Region + District label data for the map (fetch only, doesn't
 * update the map - mirrors loadNeighborhoodsData's split from
 * map.setNeighborhoods). Both come back with label_lat/label_lng (falling
 * back to center_lat/center_lng server-side isn't done - Map.ts handles
 * that fallback itself) so the label markers can be placed in one shot.
 */
async function loadGeographyData(): Promise<void> {
  try {
    const [countyResults, regionResults, districtResults] = await Promise.all([
      api.getCounties(),
      api.getRegions(),
      api.getDistricts(),
    ]);
    counties = countyResults.map(co => ({
      id: co.id,
      slug: co.id,
      name: co.name,
      centerLat: co.centerLat,
      centerLng: co.centerLng,
      labelLat: co.labelLat,
      labelLng: co.labelLng,
      neighborhoodCount: co.neighborhoodCount,
      forSaleCount: co.forSaleCount,
    }));
    regions = regionResults.map(r => ({
      id: r.id,
      slug: r.slug,
      name: r.name,
      centerLat: r.centerLat,
      centerLng: r.centerLng,
      labelLat: r.labelLat,
      labelLng: r.labelLng,
      neighborhoodCount: r.neighborhoodCount,
      forSaleCount: r.forSaleCount,
    }));
    districts = districtResults.map(d => ({
      id: d.id,
      slug: d.slug,
      name: d.name,
      regionId: d.regionId,
      regionName: d.regionName,
      centerLat: d.centerLat,
      centerLng: d.centerLng,
      labelLat: d.labelLat,
      labelLng: d.labelLng,
      polygon: d.polygon,
      neighborhoodCount: d.neighborhoodCount,
      forSaleCount: d.forSaleCount,
    }));
    console.log(`Loaded ${counties.length} counties, ${regions.length} regions, ${districts.length} districts`);
  } catch (error) {
    console.error('Failed to load county/region/district data:', error);
  }
}

/**
 * Load neighborhoods for map display (legacy - fetches and updates map)
 */
async function loadNeighborhoods(): Promise<void> {
  await loadNeighborhoodsData();
  // Set neighborhoods on map
  if (map) {
    map.setNeighborhoods(neighborhoods);
  }
}

/**
 * Shared by loadMapMarkers and loadListingsForDrawer: which MLS-vocabulary
 * statuses need to be fetched, given the current CNTL status toggles plus
 * whatever an active attribute (New/Open House/Favorites) implicitly
 * requires (those are typically Active listings, so Active gets pulled in
 * even if the For-Sale toggle itself is off).
 */
function computeStatusesToLoad(): string[] {
  if (!cntlDrawer) return [];
  const statusList = cntlDrawer.getActiveStatuses();
  const attributes = cntlDrawer.getAttributes();
  const statusesToLoad = [...statusList];

  // An id-set attribute's members (Favorites/Alerts/Agent Picks/Featured)
  // can be any status - a Featured pick doesn't have to still be Active
  // (found 2026-10-26 via apps/agent's identical function: only
  // force-adding 'Active' silently dropped a Pending relist of a
  // superseded Featured pick from both the map and Listings drawer, even
  // though listingItemKey()'s tmk-based match would've caught it once
  // fetched). Broaden to every status when any id-set attribute is on.
  const hasIdSetAttribute = ATTRIBUTE_REGISTRY.some((entry) => entry.kind === 'id-set' && attributes[entry.id]);
  if (hasIdSetAttribute) {
    for (const status of ['Active', 'Pending', 'Sold', 'OffMarket']) {
      if (!statusesToLoad.includes(status)) statusesToLoad.push(status);
    }
  } else {
    // Data-predicate attributes (Open House, New Listing, Price Change)
    // only ever apply to Active listings.
    const hasAttributes = ATTRIBUTE_REGISTRY.some((entry) => attributes[entry.id]);
    if (hasAttributes && !statusesToLoad.includes('Active')) {
      statusesToLoad.push('Active');
    }
  }
  return statusesToLoad;
}

// tmk_status collapses Cancelled/Withdrawn/Expired into one OffMarket
// bucket - they already render identically (see STATUS_COLORS in Map.ts,
// all three are the same gray), so picking one representative value back
// is lossless for anything that actually matters visually.
const MLS_STATUS_TO_TMK_STATUS: Record<string, string> = {
  Active: 'ForSale',
  Pending: 'Pending',
  Sold: 'RecentlySold',
  OffMarket: 'OffMarket',
};
const TMK_STATUS_TO_MLS_STATUS: Record<string, ListingSummary['status']> = {
  ForSale: 'Active',
  Pending: 'Pending',
  RecentlySold: 'Sold',
  OffMarket: 'Withdrawn',
};

/**
 * Adapt a precomputed tmk_status marker into the ListingSummary shape the
 * map's clustering/marker-rendering code already expects, so switching the
 * map's data source didn't require touching Map.ts at all. Fields tmk_status
 * doesn't carry (photos, exact address components beyond what's
 * denormalized, etc.) are simply absent - confirmed nothing in the
 * clustering/badge/click-to-modal path touches them; the modal itself
 * always does its own full-detail fetch regardless of what triggered it.
 */
function tmkStatusMarkerToListingSummary(marker: TmkStatusMarker): ListingSummary {
  const id = marker.listingId || marker.tmk;
  const summary = {
    id,
    listingId: id,
    tmkNormalized: marker.tmk,
    neighborhoodId: marker.neighborhoodId ?? undefined,
    status: TMK_STATUS_TO_MLS_STATUS[marker.status] ?? 'Withdrawn',
    listPrice: marker.listPrice ?? marker.salePrice ?? 0,
    daysOnMarket: marker.daysOnMarket,
    listDate: marker.listingDate,
    bedrooms: marker.bedroomsTotal,
    bathrooms: marker.bathroomsTotal,
    livingArea: marker.livingArea,
    yearBuilt: marker.yearBuilt,
    address: marker.address ?? '',
    unitNumber: marker.unitName,
    lat: marker.lat,
    lng: marker.lng,
    unitName: marker.unitName,
    propertyType: marker.propertyType,
    openHouse: marker.hasOpenHouse ? { date: marker.openHouseDate ?? '' } : undefined,
    hasPriceChange: marker.hasPriceChange,
    priceChangeDate: marker.priceChangeDate,
    previousPrice: marker.previousPrice,
  };
  return summary as ListingSummary;
}

/**
 * Fast path: fetch map bubbles from the precomputed tmk_status table
 * (data-api's /listings/markers) instead of the mls_listings-direct
 * endpoint - see docs on mapMarkerListings above. Drives ONLY the map;
 * never touches `listings` or the drawer. Called at init (in parallel with
 * neighborhoods/geography, before the map itself is ready) and on every
 * subsequent status/attribute/viewport change.
 */
async function loadMapMarkers(): Promise<void> {
  if (!cntlDrawer) return;
  // Ask mode owns both `listings` and the map outright (its own query
  // already decided the exact result set) - a background refetch here
  // would silently clobber it with an unrelated dataset.
  if (askModeActive) return;

  const statusesToLoad = computeStatusesToLoad();

  if (statusesToLoad.length === 0) {
    mapMarkerListings = [];
    lastFetchedListingsBounds = null;
    lastFetchWasUnbounded = false;
    applyFiltersToListings();
    return;
  }

  try {
    const tmkStatuses = [...new Set(statusesToLoad.map((s) => MLS_STATUS_TO_TMK_STATUS[s] ?? s))];
    const fetchBounds = getListingsFetchBounds();
    const response = await api.getMarkers({ status: tmkStatuses.join(','), bounds: fetchBounds });
    mapMarkerListings = response.markers.map(tmkStatusMarkerToListingSummary);
    lastFetchedListingsBounds = fetchBounds ?? null;
    lastFetchedListingsAt = Date.now();
    lastFetchWasUnbounded = !fetchBounds;
    console.log(`Loaded ${mapMarkerListings.length} map markers (statuses: ${statusesToLoad.join(', ')})`);
    applyFiltersToListings();
  } catch (error) {
    console.error('Failed to load map markers:', error);
  }
}

/**
 * Slow path: fetch the richer per-listing data (address, photos, exact
 * specs) the Listings Drawer needs, from the same mls_listings-direct
 * endpoint the map used to use. Deliberately NOT awaited by callers on the
 * critical path - the map already has bubbles from loadMapMarkers by the
 * time this resolves. Sets drawerListingsReady so applyFiltersToListings
 * knows to stop showing the drawer's loading spinner.
 */
async function loadListingsForDrawer(): Promise<void> {
  if (!cntlDrawer) return;
  if (askModeActive) return;

  const statusesToLoad = computeStatusesToLoad();

  if (statusesToLoad.length === 0) {
    listings = [];
    drawerListingsReady = true;
    applyFiltersToListings();
    return;
  }

  try {
    const statusParam = statusesToLoad.join(',');
    const fetchBounds = getListingsFetchBounds();
    listings = await api.getMapMarkers({ status: statusParam, bounds: fetchBounds });
    drawerListingsReady = true;
    console.log(`Loaded ${listings.length} listings for drawer (statuses: ${statusesToLoad.join(', ')})`);
    applyFiltersToListings();
  } catch (error) {
    console.error('Failed to load listings for drawer:', error);
    drawerListingsReady = true; // don't spin forever on a failed fetch
    applyFiltersToListings();
  }
}

/**
 * Refresh both the map (fast) and the drawer (slow, backgrounded) after a
 * status/attribute/viewport change. The map updates as soon as
 * loadMapMarkers resolves; the drawer shows its loading state again until
 * loadListingsForDrawer independently catches up.
 */
function reloadListings(): void {
  drawerListingsReady = false;
  applyFiltersToListings(); // flips the drawer to "loading" immediately
  void loadMapMarkers();
  void loadListingsForDrawer();
}

/**
 * Handle neighborhoods toggle from CNTL drawer
 * Now only affects map display, not listings drawer (which uses sort dropdown)
 */
function handleNeighborhoodsToggle(enabled: boolean): void {
  neighborhoodsEnabled = enabled;

  // Update map only - listings drawer is now controlled by sort dropdown
  if (map) {
    map.setNeighborhoodsVisible(enabled);
  }

  // Exit neighborhood mode if disabling
  if (!enabled && currentNeighborhoodMode) {
    if (map) {
      map.exitNeighborhoodMode();
    }
    currentNeighborhoodMode = null;
  }

  console.log('Neighborhoods on map:', enabled);
}

/**
 * Handle neighborhood click on map - enters neighborhood mode
 */
function handleNeighborhoodMapClick(neighborhood: NeighborhoodSummary): void {
  // Neighborhood mode is mutually exclusive with Ask mode (same reasoning
  // as activateAskMode's own exitNeighborhoodMode call) - clear Ask first
  // so `listings` below is the real full dataset, not Ask's narrow subset.
  if (askModeActive) {
    filterDrawer?.setAskActive(false);
    clearAskModeState();
  }

  if (neighborhoodsEnabled && map) {
    map.enterNeighborhoodMode(neighborhood.id);
    currentNeighborhoodMode = { id: neighborhood.id, name: neighborhood.name };
    // applyFiltersToListings() now scopes the drawer to currentNeighborhoodMode
    // itself (see its own comment) - covers price/bed filters and sort too,
    // which the old inline version here didn't.
    applyFiltersToListings();
  }
}

/**
 * Handle neighborhood mode exit (from zooming out or other)
 */
function handleNeighborhoodModeExit(): void {
  currentNeighborhoodMode = null;

  // Restore full listings view
  applyFiltersToListings();
}

/**
 * Handle floating label click in neighborhood mode - opens neighborhood modal
 */
function handleFloatingLabelClick(neighborhood: NeighborhoodSummary): void {
  if (neighborhoodModal) {
    neighborhoodModal.open({
      id: neighborhood.id,
      name: neighborhood.name,
      regionName: neighborhood.regionName,
      primaryImageUrl: neighborhood.primaryImageUrl,
      totalUnits: neighborhood.totalUnits,
      forSaleCount: neighborhood.forSaleCount,
    });
  }
}

/**
 * Load buildings for a neighborhood (for neighborhood mode display)
 */
async function loadBuildingsForNeighborhood(neighborhoodId: string): Promise<Array<{ id: string; name: string; lat?: number; lng?: number; floors?: number }>> {
  try {
    // Buildings API endpoint - returns buildings with lat/lng for markers
    const response = await fetch(`${CONFIG.apiUrl}/neighborhoods/${neighborhoodId}/buildings`);
    if (!response.ok) {
      console.log('Buildings endpoint not available yet');
      return [];
    }
    const data = await response.json();
    return data.data || [];
  } catch (error) {
    console.error('Failed to load buildings:', error);
    return [];
  }
}

/**
 * Load markers from tmk_status table (NEW - uses pre-computed status and badges)
 * This is a single API call that returns all marker data needed for NHM display
 */
async function loadMarkers(params: { neighborhoodId?: string; status?: string }): Promise<TmkStatusMarker[]> {
  try {
    console.log('[NHM loadMarkers] Calling API with params:', params);
    const response = await api.getMarkers({
      neighborhoodId: params.neighborhoodId,
      status: params.status || 'ForSale,Pending,RecentlySold,OffMarket',
    });
    console.log(`[NHM loadMarkers] Received ${response.markers.length} markers`);
    return response.markers;
  } catch (error) {
    console.error('[NHM loadMarkers] Failed to load markers:', error);
    return [];
  }
}

/**
 * Handle neighborhood header click in listings drawer
 */
function handleNeighborhoodHeaderClick(neighborhoodId: string, neighborhoodName: string): void {
  // Open neighborhood modal
  if (neighborhoodModal) {
    const neighborhood = neighborhoods.find(n => n.id === neighborhoodId);
    neighborhoodModal.open({
      id: neighborhoodId,
      name: neighborhoodName,
      regionName: neighborhood?.regionName,
      primaryImageUrl: neighborhood?.primaryImageUrl,
      totalUnits: neighborhood?.totalUnits,
      forSaleCount: neighborhood?.forSaleCount,
    });
  }
}

/**
 * Handle neighborhood click (legacy - for URL routing)
 */
function handleNeighborhoodClick(neighborhood: NeighborhoodSummary): void {
  history.pushState(null, '', `/neighborhood/${neighborhood.slug}`);
  showNeighborhoodModal(neighborhood.slug);
}

/**
 * Handle listing marker click on map
 */
function handleListingClick(listing: ListingSummary): void {
  if (!propertyModal) return;
  history.pushState(null, '', `/listing/${listing.listingId}`);

  // Build straight from the listing we were already handed instead of
  // re-looking it up in `listings` (showListingModal's own source) - that
  // lookup silently failed whenever this particular listing hadn't landed
  // in the separately-fetched, independently-timed drawer data yet (a real
  // gap since the map/drawer split earlier this session: the map's own
  // pins come from the fast tmk_status path, not `listings`), leaving the
  // modal's price/address/everything blank even though the clicked pin
  // obviously had a price. Same "open with what we have, then enrich"
  // pattern handleTmkStatusMarkerClick already uses below.
  propertyModal.open(listingSummaryToPropertyModalData(listing, listing.listingId));
  if (listing.listingId) {
    loadMlsDataForModal(listing.listingId);
  }

  const lat = listing.unitLat ?? listing.lat;
  const lng = listing.unitLng ?? listing.lng;
  openPropertyMapTarget = lat != null && lng != null ? { id: listingItemKey(listing), lat, lng } : null;
}

/**
 * Handle listing click from the sidebar listings drawer - same modal-opening
 * behavior as handleListingClick, plus moving the map to that listing so
 * clicking a card in the list doesn't leave the map showing wherever it
 * happened to be scrolled.
 */
function handleSidebarListingClick(listing: ListingSummary): void {
  panMapToListingOrEnterNeighborhood(listing);
  handleListingClick(listing);
}

/**
 * If the listing's neighborhood is one of the currently-visible ones (i.e.
 * present in the `neighborhoods` array - the public API already filters
 * that list to visible=1, see workers/data-api's /neighborhoods route) and
 * the Neighborhood layer itself is on, enter neighborhood mode for it -
 * same gating handleNeighborhoodMapClick uses for a direct map click, so a
 * sidebar click ends up in the same place a map click would.
 * enterNeighborhoodMode does its own fitBounds to the whole neighborhood
 * polygon, which would immediately override a plain panTo, so this skips
 * straight to it instead of panning first. Only listings with no visible
 * neighborhood (hidden/staged neighborhood, or a stale/unmapped
 * neighborhoodId) fall back to a bare pan to the listing's own coordinates.
 */
function panMapToListingOrEnterNeighborhood(listing: ListingSummary): void {
  if (!map) return;

  const neighborhood = listing.neighborhoodId
    ? neighborhoods.find(n => n.id === listing.neighborhoodId)
    : undefined;

  if (neighborhoodsEnabled && neighborhood) {
    // Neighborhood mode is mutually exclusive with Ask mode (same reasoning
    // as handleNeighborhoodMapClick's identical guard).
    if (askModeActive) {
      filterDrawer?.setAskActive(false);
      clearAskModeState();
    }
    map.enterNeighborhoodMode(neighborhood.id);
    currentNeighborhoodMode = { id: neighborhood.id, name: neighborhood.name };
    applyFiltersToListings();
    return;
  }

  const lat = listing.unitLat ?? listing.lat;
  const lng = listing.unitLng ?? listing.lng;
  if (lat != null && lng != null) {
    map.panTo({ lat, lng });
  }
}

/**
 * Handle TmkStatusMarker click in NHM mode
 * Opens PropertyModal with TMK data, loads MLS data if available
 */
function handleTmkStatusMarkerClick(marker: TmkStatusMarker): void {
  if (!propertyModal) return;

  // Map tmk_status status to PropertyDisplayStatus
  const statusMap: Record<string, PropertyModalData['status']> = {
    'ForSale': 'for-sale',
    'Pending': 'pending',
    'RecentlySold': 'sold',
    'OffMarket': 'off-market',
  };

  // Build PropertyModalData from TmkStatusMarker
  const propertyData: PropertyModalData = {
    id: marker.tmk,
    tmkNormalized: marker.tmk,
    listingId: marker.listingId,
    neighborhoodId: marker.neighborhoodId || undefined,
    status: statusMap[marker.status] || 'off-market',
    address: marker.address || `TMK ${marker.tmk}`,
    unit: marker.unitName,

    // Basic details - already on TmkStatusMarker for CNTL filter purposes
    // (migrations 040/047), just never surfaced in the modal before
    // (per Todd 2026-10-24: "we don't show much for off-market properties,
    // but we do have some TMK data"). PropertySections/PropertyModal
    // already read property.bedrooms/bathrooms/sqft/yearBuilt with a
    // listing fallback, so this is pure wiring, no rendering changes.
    bedrooms: marker.bedroomsTotal,
    bathrooms: marker.bathroomsTotal,
    sqft: marker.livingArea,
    yearBuilt: marker.yearBuilt,

    // Pricing based on status
    listPrice: marker.listPrice,
    closePrice: marker.salePrice,
    closeDate: marker.saleDate,
    daysOnMarket: marker.daysOnMarket,
    hasPriceChange: marker.hasPriceChange,
    priceChangeDate: marker.priceChangeDate,
    previousPrice: marker.previousPrice,

    // Sales history for recently sold
    sales: marker.salePrice && marker.saleDate ? [{
      saleDate: marker.saleDate,
      salePrice: marker.salePrice,
    }] : undefined,
  };

  // Update URL based on whether we have a listing ID
  if (marker.listingId) {
    history.pushState(null, '', `/listing/${marker.listingId}`);
  } else {
    history.pushState(null, '', `/property/${marker.tmk}`);
  }

  // Open modal with basic data first
  propertyModal.open(propertyData);

  // If there's a listing ID (ForSale/Pending), load full MLS data
  if (marker.listingId) {
    loadMlsDataForModal(marker.listingId);
  }

  // id must be marker.tmk, not marker.listingId (the public MLS# - doesn't
  // match anything) or ListingSummary.id (a different identity space
  // entirely) - see Map.highlightListing's comment.
  openPropertyMapTarget = { id: marker.tmk, lat: marker.lat, lng: marker.lng };
}

/**
 * Load MLS data and update the property modal
 */
async function loadMlsDataForModal(listingId: string): Promise<void> {
  if (!propertyModal) return;

  try {
    const listing = await api.getListing(listingId);
    if (listing) {
      propertyModal.updateWithMlsData(listing);
    }
  } catch (error) {
    console.error('Failed to load MLS data for modal:', error);
  }
}

/**
 * Show neighborhood modal
 */
async function showNeighborhoodModal(slug: string): Promise<void> {
  const modal = document.getElementById('neighborhood-modal');
  const detail = document.getElementById('neighborhood-detail');
  if (!modal || !detail) return;

  modal.classList.remove('hidden');

  try {
    const neighborhood = await api.getNeighborhood(slug);

    detail.innerHTML = `
      <div class="neighborhood-header">
        <h2>${formatNeighborhoodName(neighborhood.name)}</h2>
        <p class="region">${neighborhood.regionId || ''}</p>
      </div>

      <div class="neighborhood-tabs">
        <button class="tab active" data-tab="overview">Overview</button>
        <button class="tab" data-tab="market">Market</button>
        <button class="tab" data-tab="listings">Listings</button>
      </div>

      <div class="tab-content" id="tab-overview">
        <p>${neighborhood.description || 'No description available.'}</p>

        <div class="neighborhood-stats">
          ${neighborhood.totalUnits ? `<div class="stat"><span class="stat-value">${neighborhood.totalUnits}</span><span class="stat-label">Units</span></div>` : ''}
          ${neighborhood.yearBuilt ? `<div class="stat"><span class="stat-value">${neighborhood.yearBuilt}</span><span class="stat-label">Year Built</span></div>` : ''}
          ${neighborhood.strAllowed ? '<div class="stat"><span class="stat-value">Yes</span><span class="stat-label">STR Allowed</span></div>' : ''}
        </div>

        ${neighborhood.amenities?.length ? `
          <div class="amenities">
            <h4>Amenities</h4>
            <ul>${neighborhood.amenities.map((a) => `<li>${a}</li>`).join('')}</ul>
          </div>
        ` : ''}
      </div>

      <div class="tab-content hidden" id="tab-market">
        <p>Loading market data...</p>
      </div>

      <div class="tab-content hidden" id="tab-listings">
        <p>Loading listings...</p>
      </div>
    `;

    setupTabs(detail);

    if (map) {
      map.selectNeighborhood(neighborhood.id);
      map.panTo({ lat: neighborhood.centerLat, lng: neighborhood.centerLng });
    }
  } catch (error) {
    detail.innerHTML = '<p>Failed to load neighborhood details.</p>';
  }

  const closeBtn = modal.querySelector('.modal-close');
  const overlay = modal.querySelector('.modal-overlay');

  closeBtn?.addEventListener('click', closeNeighborhoodModal);
  overlay?.addEventListener('click', closeNeighborhoodModal);
}

function closeNeighborhoodModal(): void {
  const modal = document.getElementById('neighborhood-modal');
  modal?.classList.add('hidden');

  if (map) {
    map.selectNeighborhood(null);
  }

  history.pushState(null, '', '/');
}

function setupTabs(container: HTMLElement): void {
  const tabs = container.querySelectorAll('.tab');
  const contents = container.querySelectorAll('.tab-content');

  tabs.forEach((tab) => {
    tab.addEventListener('click', () => {
      const tabId = (tab as HTMLElement).dataset.tab;

      tabs.forEach((t) => t.classList.remove('active'));
      tab.classList.add('active');

      contents.forEach((c) => {
        c.classList.toggle('hidden', c.id !== `tab-${tabId}`);
      });
    });
  });
}

/**
 * Handle neighborhood route from deep link
 */
async function handleNeighborhoodRoute(slug: string): Promise<void> {
  // The router fires this immediately for the page's initial URL (see
  // router.ts's initRouter), so a clean-URL landing on /neighborhood/:slug
  // can race the background full-polygon load same as the other deep-link
  // paths - Neighborhood Mode entry below needs a real polygon.
  await ensureFullNeighborhoodsLoaded();
  // Find neighborhood by slug to open modal with full data
  const neighborhood = neighborhoods.find(n => n.slug === slug);
  if (neighborhood && neighborhoodModal) {
    neighborhoodModal.open({
      id: neighborhood.id,
      name: neighborhood.name,
      regionName: neighborhood.regionName,
      primaryImageUrl: neighborhood.primaryImageUrl,
      totalUnits: neighborhood.totalUnits,
      forSaleCount: neighborhood.forSaleCount,
    });
    // Also enter neighborhood mode on map
    if (map) {
      map.enterNeighborhoodMode(neighborhood.id);
      currentNeighborhoodMode = { id: neighborhood.id, name: neighborhood.name };
      applyFiltersToListings();
    }
  } else {
    // Fallback to legacy modal if neighborhood not found in loaded data
    showNeighborhoodModal(slug);
  }
}

/**
 * Handle listing route from deep link
 */
function handleListingRoute(mlsId: string): void {
  // Same reasoning as openListingFromQuery's identical follow-up call:
  // showListingModal's local-array lookup can miss (Sold/filtered-out/not
  // yet fetched), so always fetch full MLS detail afterward rather than
  // risking a modal stuck showing blank price/details.
  showListingModal(mlsId);
  loadMlsDataForModal(mlsId);
}

/**
 * Handle property route from deep link (TMK-based)
 */
async function handlePropertyRoute(tmk: string): Promise<void> {
  if (!propertyModal) return;

  // Try to get the property data from markers API
  try {
    const response = await api.getMarkers({ tmk });
    const marker = response.markers[0];

    if (marker) {
      // Map tmk_status status to PropertyDisplayStatus
      const statusMap: Record<string, PropertyModalData['status']> = {
        'ForSale': 'for-sale',
        'Pending': 'pending',
        'RecentlySold': 'sold',
        'OffMarket': 'off-market',
      };

      const propertyData: PropertyModalData = {
        id: marker.tmk,
        tmkNormalized: marker.tmk,
        listingId: marker.listingId,
        neighborhoodId: marker.neighborhoodId || undefined,
        status: statusMap[marker.status] || 'off-market',
        address: marker.address || `TMK ${marker.tmk}`,
        unit: marker.unitName,
        bedrooms: marker.bedroomsTotal,
        bathrooms: marker.bathroomsTotal,
        sqft: marker.livingArea,
        yearBuilt: marker.yearBuilt,
        listPrice: marker.listPrice,
        closePrice: marker.salePrice,
        closeDate: marker.saleDate,
        daysOnMarket: marker.daysOnMarket,
        hasPriceChange: marker.hasPriceChange,
        priceChangeDate: marker.priceChangeDate,
        previousPrice: marker.previousPrice,
        sales: marker.salePrice && marker.saleDate ? [{
          saleDate: marker.saleDate,
          salePrice: marker.salePrice,
        }] : undefined,
      };

      propertyModal.open(propertyData);

      // id must be marker.tmk - see handleTmkStatusMarkerClick's comment.
      if (marker.lat != null && marker.lng != null) {
        openPropertyMapTarget = { id: marker.tmk, lat: marker.lat, lng: marker.lng };
      }

      // Center map on the property
      if (map && marker.lat && marker.lng) {
        const gmap = map.getMap();
        if (gmap) {
          gmap.setCenter({ lat: marker.lat, lng: marker.lng });
          gmap.setZoom(18);
        }
      }

      // Load full MLS data if available
      if (marker.listingId) {
        loadMlsDataForModal(marker.listingId);
      }
    } else {
      console.warn(`Property not found for TMK: ${tmk}`);
    }
  } catch (error) {
    console.error('Failed to load property:', error);
  }
}

/**
 * Handle home route - close any open modals and reset state
 */
function handleHomeRoute(): void {
  // Close modals
  propertyModal?.close();
  neighborhoodModal?.close();

  // Exit neighborhood mode if active
  if (currentNeighborhoodMode && map) {
    map.exitNeighborhoodMode();
    currentNeighborhoodMode = null;
  }
}

/**
 * Maps a ListingSummary (or nothing, if the caller only has a bare ID) to
 * PropertyModalData. Shared by showListingModal's lookup-by-ID path and
 * handleListingClick's direct-from-click path, so the two can't drift out
 * of field coverage the way they used to.
 */
function listingSummaryToPropertyModalData(listingSummary: ListingSummary | undefined, mlsId: string): PropertyModalData {
  return {
    id: listingSummary?.id || mlsId,
    listingId: mlsId,
    // Without this, the alert bell is a dead click for every property
    // opened through this path (the normal map-pin/search/card flow) -
    // handleAlertToggle bails out on a falsy tmkNormalized before doing
    // anything at all, including the login check. Falls back to id (the
    // internal mls_listings PK) only if tmkNormalized is somehow still
    // missing, matching ListingsDrawer's own fallback.
    tmkNormalized: listingSummary?.tmkNormalized || listingSummary?.id,
    neighborhoodId: listingSummary?.neighborhoodId,
    neighborhoodName: listingSummary?.neighborhoodName,
    status:
      listingSummary?.status === 'Active'
        ? 'for-sale'
        : listingSummary?.status === 'Pending'
          ? 'pending'
          : listingSummary?.status === 'Sold'
            ? 'sold'
            : 'for-sale',
    address: listingSummary?.address || '',
    unit: listingSummary?.unitNumber,
    buildingName: listingSummary?.buildingName,
    city: listingSummary?.city,
    bedrooms: listingSummary?.bedrooms,
    bathrooms: listingSummary?.bathrooms,
    sqft: listingSummary?.livingArea,
    listPrice: listingSummary?.listPrice,
    originalListPrice: listingSummary?.originalListPrice,
    daysOnMarket: listingSummary?.daysOnMarket,
    primaryPhotoUrl: listingSummary?.primaryPhotoUrl || listingSummary?.photoUrl,
    hasPriceChange: listingSummary?.hasPriceChange,
    priceChangeDate: listingSummary?.priceChangeDate,
    previousPrice: listingSummary?.previousPrice,
  };
}

/**
 * Show listing modal using PropertyModal component. Only ever called with
 * a bare ID (deep links, browser back/forward) - callers that already have
 * a ListingSummary in hand (map/drawer/neighborhood-modal clicks) skip this
 * and build PropertyModalData directly instead, see handleListingClick.
 */
async function showListingModal(mlsId: string): Promise<void> {
  if (!propertyModal) return;

  // Check the fast map-pin data too, not just the slower drawer array - a
  // listing can be on the map before the drawer's own fetch has caught up.
  const listingSummary = listings.find((l) => l.listingId === mlsId)
    ?? mapMarkerListings.find((l) => l.listingId === mlsId);

  propertyModal.open(listingSummaryToPropertyModalData(listingSummary, mlsId));

  // listingSummary can be undefined here (not yet in either cached array) -
  // in that case we simply have nothing to recenter/highlight on close.
  const lat = listingSummary?.unitLat ?? listingSummary?.lat;
  const lng = listingSummary?.unitLng ?? listingSummary?.lng;
  openPropertyMapTarget = listingSummary && lat != null && lng != null
    ? { id: listingItemKey(listingSummary), lat, lng }
    : null;
}

/**
 * Setup search
 * Searches: Neighborhoods, MLS Listings, TMK Records
 */
function setupSearch(): void {
  const input = document.getElementById('search-input') as HTMLInputElement;
  const results = document.getElementById('search-results');
  const clearBtn = document.getElementById('search-clear');
  if (!input || !results) return;

  let debounceTimer: number;

  const updateClearButton = () => {
    if (clearBtn) {
      clearBtn.classList.toggle('hidden', input.value.length === 0);
    }
  };

  // Format price for display
  const formatPrice = (price: number) => {
    if (price >= 1000000) {
      return `$${(price / 1000000).toFixed(2)}M`;
    }
    return `$${(price / 1000).toFixed(0)}K`;
  };

  // Render a single search result
  const renderResult = (s: {
    type: string;
    value: string;
    id?: string;
    slug?: string;
    lat?: number;
    lng?: number;
    listingId?: string;
    listPrice?: number;
    status?: string;
    tmkNormalized?: string;
    bedrooms?: number;
    bathrooms?: number;
    sqft?: number;
    lastSaleDate?: string;
    lastSalePrice?: number;
    neighborhoodId?: string;
  }) => {
    // Build subtitle based on type
    let subtitle = '';
    if (s.type === 'listing' && s.listPrice) {
      subtitle = `${formatPrice(s.listPrice)} • ${s.status || 'Active'}`;
      if (s.listingId) subtitle += ` • MLS# ${s.listingId}`;
    } else if (s.type === 'tmk') {
      const parts: string[] = [];
      if (s.bedrooms) parts.push(`${s.bedrooms}bd`);
      if (s.bathrooms) parts.push(`${s.bathrooms}ba`);
      if (s.sqft) parts.push(`${s.sqft.toLocaleString()} sqft`);
      if (s.lastSalePrice && s.lastSaleDate) {
        const year = s.lastSaleDate.substring(0, 4);
        parts.push(`Sold ${formatPrice(s.lastSalePrice)} (${year})`);
      }
      subtitle = parts.join(' • ') || 'TMK Record';
    }

    // Type label styling
    const typeColors: Record<string, string> = {
      neighborhood: '#16a34a', // green
      listing: '#2563eb',      // blue
      tmk: '#6b7280',          // gray
    };
    const typeLabels: Record<string, string> = {
      neighborhood: 'Neighborhood',
      listing: 'Listing',
      tmk: 'Property',
    };

    return `
      <div class="search-result"
           data-type="${s.type}"
           data-id="${s.id || ''}"
           data-slug="${s.slug || ''}"
           data-lat="${s.lat || ''}"
           data-lng="${s.lng || ''}"
           data-listing-id="${s.listingId || ''}"
           data-tmk="${s.tmkNormalized || ''}"
           data-neighborhood-id="${s.neighborhoodId || ''}"
           data-bedrooms="${s.bedrooms || ''}"
           data-bathrooms="${s.bathrooms || ''}"
           data-value="${s.value}">
        <div style="display: flex; justify-content: space-between; align-items: flex-start;">
          <div style="flex: 1; min-width: 0;">
            <div style="font-weight: 500; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;">${s.value}</div>
            ${subtitle ? `<div style="font-size: 0.75rem; color: #6b7280; margin-top: 2px;">${subtitle}</div>` : ''}
          </div>
          <span style="background: ${typeColors[s.type] || '#6b7280'}; color: white; padding: 2px 6px; border-radius: 4px; font-size: 0.65rem; font-weight: 600; margin-left: 8px; white-space: nowrap;">
            ${typeLabels[s.type] || s.type}
          </span>
        </div>
      </div>
    `;
  };

  // Pinned first entry in every non-empty dropdown - Ask is reached only
  // through the search box now (no separate button/overlay-trigger icon).
  // As a query gets more sentence-like, real matches below this thin out
  // to nothing on their own (existing FTS/LIKE behavior in /search/suggest)
  // - no separate "which mode did they want" detection needed.
  const renderAskRow = (query: string): string => `
    <div class="search-result search-ask-row" data-role="ask" data-query="${escapeHtmlText(query)}">
      <svg class="search-ask-row-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
        <path d="M12 3l1.6 4.8L18 9l-4.4 1.6L12 15l-1.6-4.4L6 9l4.4-1.2L12 3z"></path>
        <path d="M19 14l0.8 2.2L22 17l-2.2 0.8L19 20l-0.8-2.2L16 17l2.2-0.8L19 14z"></path>
      </svg>
      <span>Ask</span>
    </div>
  `;

  // Shown under the Ask row while the real search fetch is in flight, so
  // the wait doesn't read as "nothing else matched."
  const renderLoadingRow = (): string => `
    <div class="search-loading-row">Searching…</div>
  `;

  const triggerAsk = (query: string) => {
    results.classList.remove('visible');
    openAskOverlay(query);
  };

  const bindResultClicks = () => {
    results.querySelectorAll('.search-result').forEach((el) => {
      el.addEventListener('click', () => {
        const dataset = (el as HTMLElement).dataset;
        if (dataset.role === 'ask') {
          triggerAsk(dataset.query || input.value.trim());
          return;
        }
        const type = dataset.type;
        const id = dataset.id;
        const lat = parseFloat(dataset.lat || '');
        const lng = parseFloat(dataset.lng || '');
        const value = dataset.value;
        const listingId = dataset.listingId || undefined;
        const tmkNormalized = dataset.tmk || undefined;
        const neighborhoodId = dataset.neighborhoodId || undefined;
        const bedrooms = dataset.bedrooms ? parseInt(dataset.bedrooms, 10) : undefined;
        const bathrooms = dataset.bathrooms ? parseFloat(dataset.bathrooms) : undefined;

        input.value = value || '';
        updateClearButton();
        results.classList.remove('visible');

        if (type === 'neighborhood' && id) {
          // Enter NHM for the neighborhood (show map, not modal)
          if (map) {
            map.enterNeighborhoodMode(id);
            currentNeighborhoodMode = { id, name: value || '' };
            applyFiltersToListings();
          }
        } else if ((type === 'listing' || type === 'tmk') && !isNaN(lat) && !isNaN(lng)) {
          // A property that belongs to a neighborhood enters NHM instead of
          // the plain center+zoom - NHM's own fitBounds already frames it
          // sensibly (see enterNeighborhoodMode), so the two are mutually
          // exclusive rather than NHM-then-recenter undoing itself.
          if (neighborhoodId && map) {
            const nh = neighborhoods.find((n) => n.id === neighborhoodId);
            map.enterNeighborhoodMode(neighborhoodId);
            currentNeighborhoodMode = { id: neighborhoodId, name: nh?.name ?? value ?? '' };
            applyFiltersToListings();
          } else if (map) {
            // Center map on the property and zoom to level 18 (shows off-market icons)
            const gmap = map.getMap();
            if (gmap) {
              gmap.setCenter({ lat, lng });
              gmap.setZoom(18);
            }
          }

          // Show the Property Info window too - a search selection used to
          // only move the map, leaving the user to separately click the
          // pin. listingId results reuse showListingModal (it fetches/
          // refreshes full data itself); bare tmk results (no active
          // listing) get a best-effort open from just what search
          // returned, same pattern as handleTmkStatusMarkerClick - sales
          // history fills in async via PropertyModal's own onLoadSalesHistory.
          if (listingId) {
            void showListingModal(listingId);
          } else if (tmkNormalized && propertyModal) {
            propertyModal.open({
              id: tmkNormalized,
              tmkNormalized,
              neighborhoodId,
              status: 'off-market',
              address: value || `TMK ${tmkNormalized}`,
              bedrooms,
              bathrooms,
            });
            openPropertyMapTarget = { id: tmkNormalized, lat, lng };
          }
        }
      });
    });
  };

  input.addEventListener('input', () => {
    updateClearButton();
    clearTimeout(debounceTimer);
    const query = input.value.trim();

    if (query.length < 2) {
      results.innerHTML = '';
      results.classList.remove('visible');
      return;
    }

    // Ask never has to wait on the network - it's always available the
    // instant there's a query, regardless of how long /search/suggest
    // takes to come back (typically ~80ms warm, but D1's tail latency can
    // occasionally run several hundred ms - no reason Ask should be
    // blocked on that).
    results.innerHTML = renderAskRow(query) + renderLoadingRow();
    results.classList.add('visible');
    bindResultClicks();

    debounceTimer = window.setTimeout(async () => {
      try {
        const suggestions = await api.searchSuggest({ q: query, limit: 5 });
        // The input may have changed again while this was in flight -
        // D1/network latency varies enough that responses can arrive out
        // of order, and a slower response for an earlier keystroke must
        // never overwrite a newer one already on screen.
        if (input.value.trim() !== query) return;
        results.innerHTML = renderAskRow(query) + suggestions.map(renderResult).join('');
        bindResultClicks();
      } catch {
        // Ask row is already showing (rendered immediately above) -
        // just drop the loading placeholder.
        if (input.value.trim() !== query) return;
        results.innerHTML = renderAskRow(query);
        bindResultClicks();
      }
    }, 200);
  });

  input.addEventListener('keydown', (e) => {
    if (e.key === 'Enter' && results.classList.contains('visible')) {
      e.preventDefault();
      const query = input.value.trim();
      if (query.length >= 2) triggerAsk(query);
    } else if (e.key === 'Escape' && results.classList.contains('visible')) {
      results.classList.remove('visible');
    }
  });

  clearBtn?.addEventListener('click', () => {
    input.value = '';
    updateClearButton();
    results.innerHTML = '';
    results.classList.remove('visible');
    input.focus();
  });

  document.addEventListener('click', (e) => {
    if (!input.contains(e.target as Node) && !results.contains(e.target as Node)) {
      results.classList.remove('visible');
    }
  });
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}
